import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx-js-style';
import {
    Plus,
    Pencil,
    Trash2,
    X,
    Save,
    Search,
    ShoppingBag,
    AlertTriangle,
    Eye,
    EyeOff,
    Loader2,
    ChevronLeft,
    ChevronRight,
    Star,
    Download,
    FileSpreadsheet,
    CheckSquare,
    Copy,
    Layers,
    ArrowUp,
    ArrowDown,
    Package,
    Receipt
} from 'lucide-react';
import Toast from '../Common/Toast';
import ConfirmDialog from '../Common/ConfirmDialog';
import './StoreManager.css';
import API_BASE_URL from '../../config/api';

const parseUtcDate = (dateStr) => new Date(dateStr.endsWith('Z') ? dateStr : dateStr + 'Z');

const stripEmailAlias = (email) => {
    if (!email) return '';
    const parts = email.split('@');
    if (parts.length !== 2) return email;
    const local = parts[0].split('+')[0];
    return `${local}@${parts[1]}`;
};

const PAYMENT_METHOD_CONFIG = {
    card: {
        label: 'Cart\u00e3o de Cr\u00e9dito / D\u00e9bito',
        icon: '💳',
        color: '#1a56db',
        bg: '#eff6ff',
        border: '#bfdbfe',
    },
    klarna: {
        label: 'Klarna — Pague a Presta\u00e7\u00f5es',
        icon: null, // SVG below
        color: '#7d1043',
        bg: '#fff0f5',
        border: '#ffb3c7',
        isKlarna: true,
    },
    multibanco: {
        label: 'Multibanco',
        icon: '🏧',
        color: '#1e3a5f',
        bg: '#f0f4ff',
        border: '#bfcfea',
    },
    mb_way: {
        label: 'MB WAY',
        icon: '📱',
        color: '#e03e2d',
        bg: '#fff5f5',
        border: '#fecaca',
    },
    sepa_debit: {
        label: 'D\u00e9bito SEPA',
        icon: '🏦',
        color: '#065f46',
        bg: '#f0fdf4',
        border: '#bbf7d0',
    },
};

function PaymentMethodCard({ method, amount }) {
    const cfg = PAYMENT_METHOD_CONFIG[method] || {
        label: method,
        icon: '💰',
        color: '#374151',
        bg: '#f9fafb',
        border: '#e5e7eb',
    };

    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            background: cfg.bg,
            border: `1.5px solid ${cfg.border}`,
            borderRadius: '10px',
            padding: '14px 18px',
        }}>
            {cfg.isKlarna ? (
                <svg viewBox="0 0 50 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: 52, height: 21, borderRadius: 4, flexShrink: 0 }}>
                    <rect width="50" height="20" rx="4" fill="#FFB3C7" />
                    <text x="50%" y="14" textAnchor="middle" fill="#17120E" fontSize="10" fontWeight="700" fontFamily="Arial,sans-serif">klarna</text>
                </svg>
            ) : (
                <span style={{ fontSize: '1.6rem', lineHeight: 1 }}>{cfg.icon}</span>
            )}
            <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, color: cfg.color, fontSize: '0.9rem' }}>{cfg.label}</div>
                {method === 'klarna' && amount && (
                    <div style={{ fontSize: '0.78rem', color: '#9d3563', marginTop: '2px' }}>
                        3 × {(amount / 3).toFixed(2)}€ sem juros
                    </div>
                )}
            </div>
            <span style={{
                background: cfg.color,
                color: '#fff',
                fontSize: '0.68rem',
                fontWeight: 700,
                padding: '3px 10px',
                borderRadius: '9999px',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                flexShrink: 0,
            }}>
                {method === 'klarna' ? 'BNPL' : 'Pago'}
            </span>
        </div>
    );
}

const StoreManager = () => {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [sports, setSports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [toasts, setToasts] = useState([]);
    const [confirmState, setConfirmState] = useState({ isOpen: false, id: null });
    const [uploadingImage, setUploadingImage] = useState(false);
    const [uploadingGalleryImage, setUploadingGalleryImage] = useState(false);

    // Order tabs & lists state
    const [activeTab, setActiveTab] = useState('products');
    const [orders, setOrders] = useState([]);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [ordersSearch, setOrdersSearch] = useState('');
    const [debouncedOrdersSearch, setDebouncedOrdersSearch] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedOrdersSearch(ordersSearch);
        }, 400);
        return () => clearTimeout(timer);
    }, [ordersSearch]);

    const [ordersStatusFilter, setOrdersStatusFilter] = useState('');
    const [ordersCurrentPage, setOrdersCurrentPage] = useState(1);
    const [ordersTotalPages, setOrdersTotalPages] = useState(1);
    const [ordersTotalCount, setOrdersTotalCount] = useState(0);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showOrderModal, setShowOrderModal] = useState(false);
    const [updatingOrderStatus, setUpdatingOrderStatus] = useState(false);
    const [modalStatusValue, setModalStatusValue] = useState('');
    const [selectedOrderIds, setSelectedOrderIds] = useState(new Set());
    const [exportingAll, setExportingAll] = useState(false);
    const [generatingInvoiceId, setGeneratingInvoiceId] = useState(null);

    // Kit Pieces state
    const [kitPieces, setKitPieces] = useState([]);
    const [kitPiecesLoading, setKitPiecesLoading] = useState(false);
    const [kitPieceSportFilter, setKitPieceSportFilter] = useState('');
    const [kitPieceSearch, setKitPieceSearch] = useState('');
    const [showKitPieceModal, setShowKitPieceModal] = useState(false);
    const [editingKitPiece, setEditingKitPiece] = useState(null);
    const [kitPieceForm, setKitPieceForm] = useState({
        name: '',
        sportId: '',
        customizationType: 'None',
        availableSizes: ['XS', 'S', 'M', 'L', 'XL', 'XXL']
    });
    const [newSizeInput, setNewSizeInput] = useState('');
    const [submittingKitPiece, setSubmittingKitPiece] = useState(false);
    const [kitPiecesSelection, setKitPiecesSelection] = useState({}); // { [id]: quantity }


    const handleImageUpload = async (e) => {
        if (!e.target.files || e.target.files.length === 0) return;
        const file = e.target.files[0];

        // Basic validation (2MB limit)
        if (file.size > 2 * 1024 * 1024) {
            addToast('A imagem é muito grande. O tamanho máximo permitido é 2MB.', 'error');
            return;
        }

        setUploadingImage(true);
        const token = localStorage.getItem('token');
        const uploadData = new FormData();
        uploadData.append('file', file);

        try {
            const response = await fetch(`${API_BASE_URL}/api/products/upload-image`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: uploadData
            });

            if (response.ok) {
                const data = await response.json();
                setFormData(prev => ({ ...prev, imageUrl: data.imageUrl }));
                addToast('Imagem carregada com sucesso!');
            } else {
                addToast('Erro ao carregar a imagem no servidor', 'error');
            }
        } catch (error) {
            console.error('Image upload error:', error);
            addToast('Erro de rede ao carregar imagem', 'error');
        } finally {
            setUploadingImage(false);
        }
    };

    const handleGalleryImageUpload = async (e) => {
        if (!e.target.files || e.target.files.length === 0) return;
        const file = e.target.files[0];

        if (file.size > 2 * 1024 * 1024) {
            addToast('A imagem é muito grande. O tamanho máximo permitido é 2MB.', 'error');
            return;
        }

        setUploadingGalleryImage(true);
        const token = localStorage.getItem('token');
        const uploadData = new FormData();
        uploadData.append('file', file);

        try {
            const response = await fetch(`${API_BASE_URL}/api/products/upload-image`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: uploadData
            });

            if (response.ok) {
                const data = await response.json();
                setFormData(prev => ({
                    ...prev,
                    images: [...(prev.images || []), { id: null, imageUrl: data.imageUrl, sortOrder: prev.images?.length || 0 }]
                }));
                addToast('Imagem da galeria carregada com sucesso!');
            } else {
                addToast('Erro ao carregar a imagem no servidor', 'error');
            }
        } catch (error) {
            console.error('Image upload error:', error);
            addToast('Erro de rede ao carregar imagem', 'error');
        } finally {
            setUploadingGalleryImage(false);
        }
    };

    const removeGalleryImage = (index) => {
        setFormData(prev => {
            const updated = [...prev.images];
            updated.splice(index, 1);
            const reindexed = updated.map((img, i) => ({ ...img, sortOrder: i }));
            return { ...prev, images: reindexed };
        });
    };

    // Filters State
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedSport, setSelectedSport] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    // Form State
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        categoryId: '',
        sportId: '',
        basePrice: '',
        memberPrice: '',
        imageUrl: '',
        isFeatured: false,
        isActive: true,
        isCustomizable: false,
        variants: [],
        images: [],
        kitItems: []
    });

    const addToast = useCallback((message, type = 'success') => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
    }, []);

    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    // Load initial reference data (categories and sports)
    useEffect(() => {
        const loadReferenceData = async () => {
            try {
                const [catsRes, sportsRes] = await Promise.all([
                    fetch(`${API_BASE_URL}/api/products/categories`),
                    fetch(`${API_BASE_URL}/api/sports`)
                ]);

                if (catsRes.ok) {
                    const catsData = await catsRes.json();
                    setCategories(catsData);
                    // Default category in form if any
                    if (catsData.length > 0) {
                        setFormData(prev => ({ ...prev, categoryId: catsData[0].id }));
                    }
                }
                if (sportsRes.ok) {
                    const sportsData = await sportsRes.json();
                    setSports(sportsData.filter(s => s.isActive));
                }
            } catch (error) {
                console.error('Error loading reference data:', error);
                addToast('Erro ao carregar categorias ou modalidades', 'error');
            }
        };

        loadReferenceData();
    }, [addToast]);

    // Load products when filters or page changes
    const fetchProducts = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (searchQuery) params.set('Search', searchQuery);
            if (selectedCategory) params.set('CategoryId', selectedCategory);
            if (selectedSport) params.set('SportId', selectedSport);
            params.set('Page', currentPage);
            params.set('PageSize', '10');
            params.set('AdminView', 'true');

            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/products?${params}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setProducts(data.items || []);
                setTotalPages(data.totalPages || 1);
                setTotalCount(data.totalCount || 0);
            } else {
                addToast('Erro ao obter a lista de produtos', 'error');
            }
        } catch (error) {
            console.error('Error fetching products:', error);
            addToast('Erro de rede ao obter produtos', 'error');
        } finally {
            setLoading(false);
        }
    }, [searchQuery, selectedCategory, selectedSport, currentPage, addToast]);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    // Fetch kit pieces
    const fetchKitPieces = useCallback(async () => {
        setKitPiecesLoading(true);
        const token = localStorage.getItem('token');
        try {
            const url = `${API_BASE_URL}/api/kit-pieces/all`;
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setKitPieces(data);
            } else {
                addToast('Erro ao obter peças de kit', 'error');
            }
        } catch (error) {
            console.error('Error fetching kit pieces:', error);
            addToast('Erro ao obter peças de kit', 'error');
        } finally {
            setKitPiecesLoading(false);
        }
    }, [addToast]);

    useEffect(() => {
        fetchKitPieces();
    }, [fetchKitPieces]);

    // Kit Piece Form handlers
    const resetKitPieceForm = () => {
        setKitPieceForm({
            name: '',
            sportId: sports.length > 0 ? sports[0].id : '',
            customizationType: 'None',
            availableSizes: ['XS', 'S', 'M', 'L', 'XL', 'XXL']
        });
        setEditingKitPiece(null);
        setNewSizeInput('');
    };

    const handleEditKitPiece = (piece) => {
        setEditingKitPiece(piece);
        setKitPieceForm({
            name: piece.name,
            sportId: piece.sportId,
            customizationType: piece.customizationType || 'None',
            availableSizes: piece.availableSizes || []
        });
        setNewSizeInput('');
        setShowKitPieceModal(true);
    };

    const handleDuplicateKitPiece = (piece) => {
        setEditingKitPiece(null);
        setKitPieceForm({
            name: `${piece.name}`,
            sportId: piece.sportId,
            customizationType: piece.customizationType || 'None',
            availableSizes: piece.availableSizes ? [...piece.availableSizes] : []
        });
        setNewSizeInput('');
        setShowKitPieceModal(true);
    };

    const handleSaveKitPiece = async (e) => {
        e.preventDefault();
        if (!kitPieceForm.name.trim()) {
            addToast('Por favor, preencha o nome da peça.', 'error');
            return;
        }
        if (!kitPieceForm.availableSizes || kitPieceForm.availableSizes.length === 0) {
            addToast('Adicione pelo menos um tamanho para a peça.', 'error');
            return;
        }

        setSubmittingKitPiece(true);
        const token = localStorage.getItem('token');
        try {
            const url = editingKitPiece
                ? `${API_BASE_URL}/api/kit-pieces/${editingKitPiece.id}`
                : `${API_BASE_URL}/api/kit-pieces`;
            const method = editingKitPiece ? 'PUT' : 'POST';

            const payload = {
                name: kitPieceForm.name.trim(),
                sportId: kitPieceForm.sportId ? parseInt(kitPieceForm.sportId) : 0,
                customizationType: kitPieceForm.customizationType || 'None',
                availableSizes: kitPieceForm.availableSizes
            };

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                addToast(editingKitPiece ? 'Peça de kit atualizada!' : 'Peça de kit criada com sucesso!');
                setShowKitPieceModal(false);
                resetKitPieceForm();
                fetchKitPieces();
            } else {
                const errData = await response.json().catch(() => ({}));
                addToast(errData.message || 'Erro ao gravar peça de kit', 'error');
            }
        } catch (error) {
            console.error('Error saving kit piece:', error);
            addToast('Erro ao comunicar com o servidor', 'error');
        } finally {
            setSubmittingKitPiece(false);
        }
    };

    const handleToggleKitPieceActive = async (piece) => {
        const token = localStorage.getItem('token');
        try {
            if (piece.isActive) {
                const res = await fetch(`${API_BASE_URL}/api/kit-pieces/${piece.id}`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    addToast(`Peça "${piece.name}" desativada.`, 'warning');
                    fetchKitPieces();
                } else {
                    addToast('Erro ao desativar peça', 'error');
                }
            } else {
                const res = await fetch(`${API_BASE_URL}/api/kit-pieces/${piece.id}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ isActive: true })
                });
                if (res.ok) {
                    addToast(`Peça "${piece.name}" reativada com sucesso!`);
                    fetchKitPieces();
                } else {
                    addToast('Erro ao reativar peça', 'error');
                }
            }
        } catch (err) {
            console.error('Toggle active error:', err);
            addToast('Erro ao alterar estado da peça', 'error');
        }
    };

    const addKitPieceSize = (sizeToAdd) => {
        const trimmed = (sizeToAdd || newSizeInput).trim().toUpperCase();
        if (!trimmed) return;
        if (kitPieceForm.availableSizes.includes(trimmed)) {
            addToast('Este tamanho já foi adicionado.', 'error');
            return;
        }
        setKitPieceForm(prev => ({
            ...prev,
            availableSizes: [...prev.availableSizes, trimmed]
        }));
        setNewSizeInput('');
    };

    const removeKitPieceSize = (sizeToRemove) => {
        setKitPieceForm(prev => ({
            ...prev,
            availableSizes: prev.availableSizes.filter(s => s !== sizeToRemove)
        }));
    };

    const applySizePreset = (sizes) => {
        setKitPieceForm(prev => ({
            ...prev,
            availableSizes: sizes
        }));
    };

    // Kit Composition handlers in Product Modal
    const handleAddPieceToProduct = () => {
        const selectedIds = Object.keys(kitPiecesSelection);
        if (selectedIds.length === 0) {
            addToast('Selecione pelo menos uma peça de kit para adicionar.', 'error');
            return;
        }

        setFormData(prev => {
            let currentItems = [...(prev.kitItems || [])];
            selectedIds.forEach(selectedId => {
                const qty = kitPiecesSelection[selectedId] || 1;
                const piece = kitPieces.find(p => p.id === parseInt(selectedId));
                if (piece) {
                    const existingIdx = currentItems.findIndex(ki => ki.kitPieceId === piece.id);
                    if (existingIdx >= 0) {
                        currentItems[existingIdx] = {
                            ...currentItems[existingIdx],
                            quantity: currentItems[existingIdx].quantity + qty
                        };
                    } else {
                        currentItems.push({
                            kitPieceId: piece.id,
                            pieceName: piece.name,
                            quantity: qty,
                            customizationType: piece.customizationType,
                            availableSizes: piece.availableSizes || [],
                            sortOrder: currentItems.length
                        });
                    }
                }
            });
            return { ...prev, kitItems: currentItems };
        });
        setKitPiecesSelection({});
    };

    const updateProductKitItemQty = (index, delta) => {
        setFormData(prev => {
            const copy = [...(prev.kitItems || [])];
            const newQty = (copy[index].quantity || 1) + delta;
            if (newQty <= 0) {
                copy.splice(index, 1);
            } else {
                copy[index] = { ...copy[index], quantity: newQty };
            }
            return { ...prev, kitItems: copy };
        });
    };

    const removeProductKitItem = (index) => {
        setFormData(prev => {
            const copy = [...(prev.kitItems || [])];
            copy.splice(index, 1);
            return { ...prev, kitItems: copy };
        });
    };

    const moveProductKitItem = (index, direction) => {
        setFormData(prev => {
            const copy = [...(prev.kitItems || [])];
            const target = index + direction;
            if (target < 0 || target >= copy.length) return prev;
            const temp = copy[index];
            copy[index] = copy[target];
            copy[target] = temp;
            return { ...prev, kitItems: copy };
        });
    };

    // Fetch orders logic
    const fetchOrders = useCallback(async () => {
        setOrdersLoading(true);
        try {
            const params = new URLSearchParams();
            if (ordersStatusFilter) params.set('status', ordersStatusFilter);
            if (debouncedOrdersSearch && debouncedOrdersSearch.trim()) params.set('search', debouncedOrdersSearch.trim());
            params.set('page', ordersCurrentPage);
            params.set('pageSize', '10');

            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/store/orders?${params}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setOrders(data.items || []);
                setOrdersTotalPages(data.totalPages || 1);
                setOrdersTotalCount(data.totalCount || 0);
            } else {
                addToast('Erro ao obter a lista de encomendas', 'error');
            }
        } catch (error) {
            console.error('Error fetching orders:', error);
            addToast('Erro de rede ao obter encomendas', 'error');
        } finally {
            setOrdersLoading(false);
        }
    }, [ordersStatusFilter, debouncedOrdersSearch, ordersCurrentPage, addToast]);

    useEffect(() => {
        if (activeTab === 'orders') {
            fetchOrders();
        }
    }, [activeTab, fetchOrders]);

    /**
     * Build the formatted Excel workbook matching the template image.
     * Groups orders by sport + team, with one row per athlete.
     * Kit piece columns are discovered dynamically from the kitPieceSelectionsJson data.
     */
    const downloadExcel = async (orderList, filename) => {
        // Filter out cancelled orders
        const validOrders = orderList.filter(o => o.status !== 'Cancelado');

        if (validOrders.length === 0) {
            console.warn('No valid orders to export after filtering cancelled ones.');
            return;
        }

        const wb = XLSX.utils.book_new();

        // Group orders by sport then by team
        const groups = {};
        for (const o of validOrders) {
            let sport = o.sportName || o.sport || '';
            let team  = o.teamName  || o.team  || '';
            
            // Fallback for orders where TeamId wasn't saved in the DB but is present in variantDescription
            if (!team && o.items) {
                for (const item of o.items) {
                    if (item.variantDescription) {
                        const match = item.variantDescription.match(/Equipa:\s*([^·]+)/);
                        if (match && match[1]) {
                            team = match[1].trim();
                            break;
                        }
                    }
                }
            }

            const key   = `${sport}||${team}`;
            if (!groups[key]) groups[key] = { sport, team, orders: [] };
            groups[key].orders.push(o);
        }

        const groupKeys = Object.keys(groups);
        if (groupKeys.length === 0) {
            _buildGenericSheet(wb, orderList);
        } else {
            // One sheet per sport, all teams together
            const sportSheets = {};
            for (const key of groupKeys) {
                const { sport, team, orders } = groups[key];
                const sheetName = sport || 'Geral';
                if (!sportSheets[sheetName]) sportSheets[sheetName] = [];
                sportSheets[sheetName].push({ team, orders });
            }

            for (const [sheetName, teamGroups] of Object.entries(sportSheets)) {
                const ws = _buildKitSheet(sheetName, teamGroups);
                const safeSheetName = sheetName.replace(/[\\/:?*[\]]/g, '').substring(0, 31);
                XLSX.utils.book_append_sheet(wb, ws, safeSheetName || 'Geral');
            }
        }

        try {
            // Two-pass approach to preserve xlsx-js-style's custom XML and embed image with exceljs
            const ExcelJS = (await import('exceljs')).default || (await import('exceljs'));
            const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
            
            const workbook = new ExcelJS.Workbook();
            await workbook.xlsx.load(buffer);

            // Fetch the logo
            let imageBuffer = null;
            try {
                const res = await fetch('/cdp_logo.png');
                if (res.ok) {
                    const blob = await res.blob();
                    imageBuffer = await blob.arrayBuffer();
                }
            } catch (err) {
                console.warn('Failed to load CDP logo for excel export', err);
            }

            if (imageBuffer) {
                const imageId = workbook.addImage({
                    buffer: imageBuffer,
                    extension: 'png',
                });
                workbook.worksheets.forEach(ws => {
                    ws.addImage(imageId, {
                        tl: { col: 0, row: 0 },
                        ext: { width: 115, height: 115 }
                    });
                });
            }

            const finalBuffer = await workbook.xlsx.writeBuffer();
            const finalBlob = new Blob([finalBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(finalBlob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            console.error('Failed to inject image via exceljs, falling back to xlsx-js-style only export:', e);
            XLSX.writeFile(wb, filename);
        }
    };

    /** Build the kit-style worksheet for a sport with multiple team groups */
    const _buildKitSheet = (sportName, teamGroups) => {
        // ── Step 1: Discover all kit pieces used across ALL orders in this sheet ──
        // Key: kitPieceId (number), Value: pieceName (string)
        // We sort by ID to preserve the order they were created in the system.
        const pieceMap = new Map();
        const categorySet = new Set();
        for (const { orders } of teamGroups) {
            for (const o of orders) {
                for (const item of (o.items || [])) {
                    if (item.kitPieceSelectionsJson) {
                        try {
                            const sels = JSON.parse(item.kitPieceSelectionsJson) || [];
                            for (const s of sels) {
                                const id   = s.KitPieceId   ?? s.kitPieceId;
                                const name = s.PieceName    || s.pieceName || `Peça ${id}`;
                                if (id != null && !pieceMap.has(id)) pieceMap.set(id, name);
                            }
                        } catch (e) { /* ignore malformed JSON */ }
                    } else if (item.categoryName) {
                        const cat = item.categoryName.toUpperCase();
                        if (cat !== 'KITS' && cat !== 'KIT') {
                            categorySet.add(cat);
                        }
                    }
                }
            }
        }

        // Sort by piece ID → same order as created in the system
        const kitCols = [...pieceMap.entries()]
            .sort((a, b) => a[0] - b[0])
            .map(([id, name]) => ({ id, name, type: 'kit' }));

        const catCols = [...categorySet].sort().map(cat => ({ id: `cat_${cat}`, name: cat, type: 'category', originalCat: cat }));

        const dynamicCols = [...kitCols, ...catCols];

        // ── Layout constants ──────────────────────────────────────────────────
        const FIXED_COLS = 3; // Nº | ATLETA | NOME CAMISOLA
        const TOTAL_COLS = FIXED_COLS + Math.max(dynamicCols.length, 1);

        const aoa        = [];
        const merges     = [];
        const rowHeights = [];
        const cellStyles = {};

        const emptyRow   = () => Array(TOTAL_COLS).fill(null);
        const markStyle  = (r, c, style) => { cellStyles[`R${r}C${c}`] = style; };

        // ── Colour palettes — alternating per team group ────────────────────
        // Palette 0 → Blue  (groups 0, 2, 4…)
        // Palette 1 → Green (groups 1, 3, 5…)
        const PALETTES = [
            {
                seccao:      { font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '1F4E79' } }, alignment: { vertical: 'center', horizontal: 'left' } },
                equipaLabel: { font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '1F4E79' } }, alignment: { vertical: 'center', horizontal: 'left' } },
                equipaValue: { font: { bold: true, sz: 12, color: { rgb: '1F4E79' } }, fill: { fgColor: { rgb: 'BDD7EE' } }, alignment: { vertical: 'center', horizontal: 'center' } },
                epocaLabel:  { font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '1F4E79' } }, alignment: { vertical: 'center', horizontal: 'right' } },
                fixedHeader: { font: { bold: true, sz: 10, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '2E75B6' } }, alignment: { vertical: 'center', horizontal: 'center', wrapText: true }, border: { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } } },
                groupHeader: { font: { bold: true, sz: 9,  color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '2E75B6' } }, alignment: { vertical: 'center', horizontal: 'center', wrapText: true }, border: { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } } },
                pieceHeader: { font: { bold: true, sz: 9,  color: { rgb: '1F4E79' } }, fill: { fgColor: { rgb: 'BDD7EE' } }, alignment: { vertical: 'center', horizontal: 'center', wrapText: true }, border: { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } } },
                kitCell:     { fill: { fgColor: { rgb: 'DEEAF1' } }, alignment: { vertical: 'center', horizontal: 'center' }, border: { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } } },
                sizeCell:    { font: { bold: true, sz: 11, color: { rgb: '1F4E79' } }, fill: { fgColor: { rgb: 'BDD7EE' } }, alignment: { vertical: 'center', horizontal: 'center', wrapText: true }, border: { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } } },
                dataFixed:   { alignment: { vertical: 'center', horizontal: 'center' }, border: { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } } },
                dorsalData:  { font: { bold: true }, alignment: { vertical: 'center', horizontal: 'center' }, border: { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } } },
            },
            {
                seccao:      { font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '1E5631' } }, alignment: { vertical: 'center', horizontal: 'left' } },
                equipaLabel: { font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '1E5631' } }, alignment: { vertical: 'center', horizontal: 'left' } },
                equipaValue: { font: { bold: true, sz: 12, color: { rgb: '1E5631' } }, fill: { fgColor: { rgb: 'C6EFCE' } }, alignment: { vertical: 'center', horizontal: 'center' } },
                epocaLabel:  { font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '1E5631' } }, alignment: { vertical: 'center', horizontal: 'right' } },
                fixedHeader: { font: { bold: true, sz: 10, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '375623' } }, alignment: { vertical: 'center', horizontal: 'center', wrapText: true }, border: { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } } },
                groupHeader: { font: { bold: true, sz: 9,  color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '375623' } }, alignment: { vertical: 'center', horizontal: 'center', wrapText: true }, border: { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } } },
                pieceHeader: { font: { bold: true, sz: 9,  color: { rgb: '1E5631' } }, fill: { fgColor: { rgb: 'C6EFCE' } }, alignment: { vertical: 'center', horizontal: 'center', wrapText: true }, border: { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } } },
                kitCell:     { fill: { fgColor: { rgb: 'EBF5EB' } }, alignment: { vertical: 'center', horizontal: 'center' }, border: { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } } },
                sizeCell:    { font: { bold: true, sz: 11, color: { rgb: '1E5631' } }, fill: { fgColor: { rgb: 'C6EFCE' } }, alignment: { vertical: 'center', horizontal: 'center', wrapText: true }, border: { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } } },
                dataFixed:   { alignment: { vertical: 'center', horizontal: 'center' }, border: { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } } },
                dorsalData:  { font: { bold: true }, alignment: { vertical: 'center', horizontal: 'center' }, border: { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } } },
            },
        ];

        const currentYear = new Date().getFullYear();
        const epocaStr    = `${currentYear}-${currentYear + 1}`;

        teamGroups.forEach(({ team, orders }, groupIdx) => {
            if (groupIdx === 0) {
                // Reserve space for the logo at the top
                aoa.push(emptyRow(), emptyRow(), emptyRow(), emptyRow());
                rowHeights.push({ idx: 0, hpt: 24 }, { idx: 1, hpt: 24 }, { idx: 2, hpt: 24 }, { idx: 3, hpt: 24 });
            }

            const P = PALETTES[groupIdx % PALETTES.length];
            // ── Row A: SECÇÃO | EQUIPA | Época ───────────────────────────────
            const rA = emptyRow();
            aoa.push(rA);
            const rAIdx = aoa.length - 1;
            
            for (let c = 0; c < TOTAL_COLS; c++) markStyle(rAIdx, c, P.seccao);

            if (TOTAL_COLS >= 6) {
                rA[0] = `SECÇÃO: ${sportName.toUpperCase()}`;
                rA[3] = 'EQUIPA:';
                rA[4] = team ? team.toUpperCase() : '';
                rA[TOTAL_COLS - 1] = `Época: ${epocaStr}`;
                
                markStyle(rAIdx, 3, P.equipaLabel);
                markStyle(rAIdx, 4, P.equipaValue);
                markStyle(rAIdx, TOTAL_COLS - 1, P.epocaLabel);
                
                merges.push({ s: { r: rAIdx, c: 0 }, e: { r: rAIdx, c: 2 } });
                if (TOTAL_COLS > 6) {
                    merges.push({ s: { r: rAIdx, c: 4 }, e: { r: rAIdx, c: TOTAL_COLS - 2 } });
                }
            } else {
                rA[0] = `SECÇÃO: ${sportName.toUpperCase()} | EQUIPA: ${team ? team.toUpperCase() : ''}`;
                rA[TOTAL_COLS - 1] = `Época: ${epocaStr}`;
                markStyle(rAIdx, TOTAL_COLS - 1, P.epocaLabel);
                if (TOTAL_COLS > 2) {
                    merges.push({ s: { r: rAIdx, c: 0 }, e: { r: rAIdx, c: TOTAL_COLS - 2 } });
                }
            }
            rowHeights.push({ idx: rAIdx, hpt: 22 });

            // ── Row B: Fixed headers (vertically merged with Row C) + "TAMANHOS" / "AVULSO" group ──
            const rB = emptyRow();
            rB[0] = 'ATLETA';
            rB[1] = 'Nº';
            rB[2] = 'NOME CAMISOLA';

            if (kitCols.length > 0) {
                rB[FIXED_COLS] = 'KITS';
            }
            if (catCols.length > 0) {
                rB[FIXED_COLS + kitCols.length] = 'PEÇAS AVULSO';
            }
            aoa.push(rB);
            const rBIdx = aoa.length - 1;

            // Fixed cols span rows B and C (vertical merge)
            [0, 1, 2].forEach(c => {
                merges.push({ s: { r: rBIdx, c }, e: { r: rBIdx + 1, c } });
                markStyle(rBIdx, c, P.fixedHeader);
            });

            // Group headers
            if (kitCols.length > 1) {
                merges.push({ s: { r: rBIdx, c: FIXED_COLS }, e: { r: rBIdx, c: FIXED_COLS + kitCols.length - 1 } });
            }
            if (catCols.length > 1) {
                merges.push({ s: { r: rBIdx, c: FIXED_COLS + kitCols.length }, e: { r: rBIdx, c: FIXED_COLS + kitCols.length + catCols.length - 1 } });
            }
            for (let c = FIXED_COLS; c < TOTAL_COLS; c++) markStyle(rBIdx, c, P.groupHeader);
            rowHeights.push({ idx: rBIdx, hpt: 22 });

            // ── Row C: Kit piece names as column sub-headers ──────────────────
            const rC = emptyRow();
            dynamicCols.forEach((col, i) => { rC[FIXED_COLS + i] = col.name; });
            aoa.push(rC);
            const rCIdx = aoa.length - 1;
            [0, 1, 2].forEach(c => markStyle(rCIdx, c, P.fixedHeader));
            for (let c = FIXED_COLS; c < TOTAL_COLS; c++) markStyle(rCIdx, c, P.pieceHeader);
            rowHeights.push({ idx: rCIdx, hpt: 36 });

            // ── Data rows: one per order × item ──────────────────────────────
            for (const o of orders) {
                if (!o.items || !Array.isArray(o.items)) continue;

                for (const item of o.items) {
                    // Parse kit selections → map pieceId → { size, cName, cNum }
                    const pieceData = {};
                    if (item.kitPieceSelectionsJson) {
                        try {
                            const sels = JSON.parse(item.kitPieceSelectionsJson) || [];
                            for (const s of sels) {
                                const id    = s.KitPieceId   ?? s.kitPieceId;
                                const size  = s.SelectedSize || s.selectedSize  || '';
                                const cName = s.CustomName   || s.customName    || '';
                                const cNum  = s.CustomNumber || s.customNumber  || '';
                                if (id != null) pieceData[id] = { size, cName, cNum };
                            }
                        } catch (e) { /* ignore */ }
                    }

                    // Cell value: size on line 1, personalisation TYPE on line 2 (not repeated values)
                    const cellValue = (col) => {
                        if (col.type === 'kit') {
                            const d = pieceData[col.id];
                            if (!d || !d.size) return '';
                            const hasName = !!d.cName;
                            const hasNum  = !!d.cNum;
                            if (hasName && hasNum) return `${d.size}\nPersonalização: Nome + Nº`;
                            if (hasNum)            return `${d.size}\nPersonalização: Nº`;
                            if (hasName)           return `${d.size}\nPersonalização: Nome`;
                            return d.size;
                        } else {
                            if (item.categoryName && item.categoryName.toUpperCase() === col.originalCat) {
                                const size = item.variantDescription || '';
                                const hasName = !!item.customName;
                                const hasNum = !!item.customNumber;
                                let pers = '';
                                if (hasName && hasNum) pers = '\nPersonalização: Nome + Nº';
                                else if (hasNum) pers = '\nPersonalização: Nº';
                                else if (hasName) pers = '\nPersonalização: Nome';

                                return `${item.productName}${size ? ` - ${size}` : ''}${pers}`;
                            }
                            return '';
                        }
                    };

                    const athleteName = (o.customerName || o.guestName || '').toUpperCase();
                    const jerseyName  = item.customName  || '';
                    const dorsal      = item.customNumber || '';

                    const dr = emptyRow();
                    dr[0] = athleteName;
                    dr[1] = dorsal;
                    dr[2] = jerseyName;
                    dynamicCols.forEach((col, i) => { dr[FIXED_COLS + i] = cellValue(col); });

                    const drIdx = aoa.length;
                    aoa.push(dr);

                    markStyle(drIdx, 0, P.dataFixed);
                    markStyle(drIdx, 1, P.dorsalData);
                    markStyle(drIdx, 2, P.dataFixed);

                    let maxLines = 1;
                    dynamicCols.forEach((col, i) => {
                        const val = cellValue(col);
                        markStyle(drIdx, FIXED_COLS + i, val ? P.sizeCell : P.kitCell);
                        
                        if (val) {
                            const maxWch = col.type === 'category' ? 35 : 18;
                            const wrappedLines = val.split('\n').reduce((acc, line) => acc + Math.ceil(line.length / maxWch), 0);
                            if (wrappedLines > maxLines) maxLines = wrappedLines;
                        }
                    });
                    rowHeights.push({ idx: drIdx, hpt: Math.max(20, maxLines * 17) });
                }
            }

            // Empty separator between teams
            aoa.push(emptyRow());
        });

        // ── Build worksheet ───────────────────────────────────────────────────
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        ws['!merges'] = merges;

        // Column widths: fixed cols + one width per dynamic piece col
        ws['!cols'] = [
            { wch: 30 },  // ATLETA
            { wch: 6  },  // Nº
            { wch: 16 },  // NOME CAMISOLA
            ...dynamicCols.map(col => ({ wch: col.type === 'category' ? 35 : 18 })),
        ];

        // Row heights
        ws['!rows'] = aoa.map((_, i) => {
            const rh = rowHeights.find(r => r.idx === i);
            return { hpt: rh ? rh.hpt : 18 };
        });

        // Apply cell styles
        for (const [key, style] of Object.entries(cellStyles)) {
            const m = key.match(/R(\d+)C(\d+)/);
            if (!m) continue;
            const r = parseInt(m[1]), c = parseInt(m[2]);
            const addr = XLSX.utils.encode_cell({ r, c });
            if (ws[addr]) ws[addr].s = style;
            else ws[addr] = { t: 's', v: '', s: style };
        }

        return ws;
    };

    /** Fallback generic sheet when no sport/team grouping data is available */
    const _buildGenericSheet = (wb, orderList) => {
        const rows = orderList.map((o) => {
            let artigosFormatados = '';
            if (o.items && Array.isArray(o.items)) {
                artigosFormatados = o.items.map(i => {
                    let desc = `${i.quantity}x ${i.productName}`;
                    if (i.variantDescription) desc += ` (${i.variantDescription})`;
                    let cust = [];
                    if (i.customName) cust.push(`Nome: ${i.customName}`);
                    if (i.customNumber) cust.push(`Nº: ${i.customNumber}`);
                    if (cust.length > 0) desc += ` [${cust.join(', ')}]`;
                    if (i.kitPieceSelectionsJson) {
                        try {
                            const parsed = JSON.parse(i.kitPieceSelectionsJson);
                            if (parsed && parsed.length > 0) {
                                const kpDetails = parsed.map(kp => {
                                    const pName = kp.PieceName || kp.pieceName || 'Peça';
                                    const pSize = kp.SelectedSize || kp.selectedSize || '';
                                    const cName = kp.CustomName || kp.customName;
                                    const cNumber = kp.CustomNumber || kp.customNumber;
                                    let kpDesc = `${pName} (${pSize})`;
                                    if (cName || cNumber) kpDesc += ` [${cName || '-'}/${cNumber || '-'}]`;
                                    return kpDesc;
                                }).join('; ');
                                desc += `\n   ↳ Kit: ${kpDetails}`;
                            }
                        } catch (e) { }
                    }
                    return desc;
                }).join('\n');
            } else {
                artigosFormatados = o.itemCount ?? '';
            }
            return {
                'ID': o.id,
                'Cliente': o.customerName || o.guestName || 'Convidado',
                'Email': stripEmailAlias(o.guestEmail || o.email || ''),
                'Data': parseUtcDate(o.createdAt).toLocaleString('pt-PT'),
                'Artigos': artigosFormatados,
                'Valor Total (€)': (o.totalAmount || 0).toFixed(2),
                'Estado': o.status,
                'Método Pagamento': o.paymentMethod || '',
                'Morada Envio': (() => {
                    if (!o.shippingAddress) return 'Levantamento no Clube';
                    try {
                        const a = JSON.parse(o.shippingAddress);
                        return [a.Street || a.street, a.City || a.city, a.PostalCode || a.postalCode, a.Country || a.country || 'Portugal'].filter(Boolean).join(', ');
                    } catch { return o.shippingAddress; }
                })(),
                'Notas': o.notes || '',
            };
        });
        const ws = XLSX.utils.json_to_sheet(rows);
        ws['!cols'] = [
            { wch: 8 }, { wch: 25 }, { wch: 32 }, { wch: 20 }, { wch: 85 },
            { wch: 18 }, { wch: 18 }, { wch: 20 }, { wch: 50 }, { wch: 30 }
        ];
        ws['!rows'] = [{ hpt: 25 }, ...rows.map(row => {
            const text = row['Artigos'] ? String(row['Artigos']) : '';
            return { hpt: Math.max(24, text.split('\n').length * 20) };
        })];
        XLSX.utils.book_append_sheet(wb, ws, 'Encomendas');
    };

    /** Export every order (fetches all from API) */
    const handleExportAll = async () => {
        setExportingAll(true);
        try {
            const token = localStorage.getItem('token');
            const params = new URLSearchParams();
            if (ordersStatusFilter) params.set('status', ordersStatusFilter);
            const res = await fetch(`${API_BASE_URL}/api/store/orders/export?${params}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) { addToast('Erro ao obter encomendas para exportar', 'error'); return; }
            const allOrders = await res.json();
            const dateStr = new Date().toISOString().slice(0, 10);
            await downloadExcel(allOrders, `encomendas_${dateStr}.xlsx`);
            addToast(`${allOrders.length} encomendas exportadas com sucesso!`);
        } catch (err) {
            console.error(err);
            addToast('Erro ao exportar encomendas', 'error');
        } finally {
            setExportingAll(false);
        }
    };

    /** Export only the orders checked in the table */
    const handleExportSelected = async () => {
        const selectedIds = Array.from(selectedOrderIds);
        if (!selectedIds.length) { addToast('Seleciona pelo menos uma encomenda', 'warning'); return; }

        setExportingAll(true);
        try {
            const token = localStorage.getItem('token');
            // Fetch full details for each selected order
            const ordersData = await Promise.all(selectedIds.map(async (id) => {
                const res = await fetch(`${API_BASE_URL}/api/store/orders/${id}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                return await res.json();
            }));

            await downloadExcel(ordersData, `encomendas_selecionadas_${Date.now()}.xlsx`);
            addToast(`${ordersData.length} encomenda(s) exportada(s)!`);
        } catch (err) {
            console.error(err);
            addToast('Erro ao exportar encomendas selecionadas', 'error');
        } finally {
            setExportingAll(false);
        }
    };

    const handleGenerateInvoice = async (orderId) => {
        setGeneratingInvoiceId(orderId);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/api/store/orders/${orderId}/invoice`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const data = await res.json();
            if (res.ok) {
                addToast(data.message || 'Fatura gerada com sucesso!', 'success');
                if (data.pdfUrl) {
                    window.open(data.pdfUrl, '_blank');
                }
                fetchOrders(); // Refresh to get the moloniDocumentId
            } else {
                addToast(data.message || 'Erro ao gerar fatura', 'error');
            }
        } catch (err) {
            console.error('Error generating invoice:', err);
            addToast('Erro ao comunicar com o servidor', 'error');
        } finally {
            setGeneratingInvoiceId(null);
        }
    };

    const handleViewInvoice = async (orderId) => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/api/store/orders/${orderId}/invoice-pdf`, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const data = await res.json();
            if (res.ok && data.pdfUrl) {
                window.open(data.pdfUrl, '_blank');
            } else {
                addToast(data.message || 'Erro ao obter PDF da fatura', 'error');
            }
        } catch (err) {
            console.error('Error fetching invoice pdf:', err);
            addToast('Erro ao comunicar com o servidor', 'error');
        }
    };

    /** Export a single order by its full detail object or summary row */
    const handleExportSingle = async (order) => {
        setExportingAll(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/api/store/orders/${order.id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const fullOrderDetails = await res.json();
            await downloadExcel([fullOrderDetails], `encomenda_${order.id}.xlsx`);
            addToast(`Encomenda #${order.id} exportada!`);
        } catch (err) {
            console.error(err);
            addToast('Erro ao exportar encomenda', 'error');
        } finally {
            setExportingAll(false);
        }
    };

    // Toggle individual checkbox
    const toggleOrderSelect = (id) => {
        setSelectedOrderIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    // Select / deselect all visible
    const toggleSelectAll = () => {
        if (selectedOrderIds.size === filteredOrders.length) {
            setSelectedOrderIds(new Set());
        } else {
            setSelectedOrderIds(new Set(filteredOrders.map(o => o.id)));
        }
    };

    const fetchOrderDetails = async (orderId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/store/orders/${orderId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setSelectedOrder(data);
                setModalStatusValue(data.status);
                setShowOrderModal(true);
            } else {
                addToast('Erro ao obter os detalhes da encomenda', 'error');
            }
        } catch (error) {
            console.error('Error fetching order details:', error);
            addToast('Erro ao carregar detalhes da encomenda', 'error');
        }
    };

    const handleUpdateOrderStatus = async (orderId, newStatus) => {
        setUpdatingOrderStatus(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/store/orders/${orderId}/status`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ status: newStatus, notes: 'Atualizado pelo administrador' })
            });

            if (response.ok) {
                addToast('Estado da encomenda atualizado com sucesso!');
                fetchOrders();
                // Refresh modal details
                const detailsRes = await fetch(`${API_BASE_URL}/api/store/orders/${orderId}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                if (detailsRes.ok) {
                    const detailedData = await detailsRes.json();
                    setSelectedOrder(detailedData);
                } else {
                    setSelectedOrder(prev => prev ? { ...prev, status: newStatus } : null);
                }
            } else {
                const err = await response.json().catch(() => ({}));
                addToast(err.message || 'Erro ao atualizar estado', 'error');
            }
        } catch (error) {
            console.error('Error updating status:', error);
            addToast('Erro ao ligar ao servidor', 'error');
        } finally {
            setUpdatingOrderStatus(false);
        }
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setCurrentPage(1);
    };

    const handleCategoryFilterChange = (e) => {
        setSelectedCategory(e.target.value);
        setCurrentPage(1);
    };

    const handleSportFilterChange = (e) => {
        setSelectedSport(e.target.value);
        setCurrentPage(1);
    };

    // Variants Handlers
    const VARIANT_TYPE_OPTIONS = ['Tamanho', 'Cor'];

    const prefillStandardSizes = () => {
        const standardSizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
        const newVariants = standardSizes.map(size => ({
            id: null,
            variantType: 'Tamanho',
            size: size,
            color: null,
            stockQty: 9999,
            sku: null,
            isActive: true
        }));
        setFormData(prev => ({
            ...prev,
            variants: newVariants
        }));
        addToast('Tamanhos padrão (XS-XXL) preenchidos!');
    };

    const addVariantRow = () => {
        setFormData(prev => ({
            ...prev,
            variants: [
                ...prev.variants,
                { id: null, variantType: 'Tamanho', size: '', color: null, stockQty: 9999, sku: null, isActive: true }
            ]
        }));
    };

    const removeVariantRow = (index) => {
        setFormData(prev => {
            const updated = [...prev.variants];
            updated.splice(index, 1);
            return { ...prev, variants: updated };
        });
    };

    const handleVariantChange = (index, field, value) => {
        setFormData(prev => {
            const updated = [...prev.variants];
            updated[index] = {
                ...updated[index],
                [field]: field === 'stockQty' ? Math.max(0, parseInt(value) || 0) : value
            };
            return { ...prev, variants: updated };
        });
    };

    // Add / Edit submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name || !formData.categoryId || formData.basePrice === '') {
            addToast('Por favor, preencha os campos obrigatórios.', 'error');
            return;
        }

        setSubmitting(true);
        const token = localStorage.getItem('token');

        // Formulate request object
        let variantsPayload = [];
        if (parseInt(formData.categoryId) === 2) {
            // Kits: composition is in kitItems. Create a default variant so order/variant FK works seamlessly
            if (!formData.sportId) {
                addToast('Os kits necessitam de uma modalidade associada.', 'error');
                setSubmitting(false);
                return;
            }
            if (!formData.kitItems || formData.kitItems.length === 0) {
                addToast('O kit necessita de pelo menos uma peça de roupa na sua composição.', 'error');
                setSubmitting(false);
                return;
            }
            const existingVariantId = (editingProduct && editingProduct.variants && editingProduct.variants.length > 0)
                ? editingProduct.variants[0].id
                : null;
            variantsPayload = [{
                id: existingVariantId,
                variantType: 'Kit',
                size: 'Único',
                color: null,
                stockQty: 9999,
                sku: null,
                isActive: true
            }];
        } else if ([1, 3].includes(parseInt(formData.categoryId))) {
            // Equipamentos ou Merchandising
            variantsPayload = formData.variants.map(v => {
                // Sanitise sentinel: if user selected Personalizado but didn't type, send null
                const sanitisedType = (v.variantType && v.variantType !== '__custom__') ? v.variantType : null;
                return {
                    id: v.id,
                    variantType: sanitisedType,
                    size: v.size || null,
                    color: null,
                    stockQty: 9999,
                    sku: null,
                    isActive: v.isActive
                };
            });
        } else {
            // Default behavior for other categories (if any)
            const existingVariantId = (editingProduct && editingProduct.variants && editingProduct.variants.length > 0)
                ? editingProduct.variants[0].id
                : null;
            variantsPayload = [{
                id: existingVariantId,
                size: null,
                color: null,
                stockQty: 9999,
                sku: null,
                isActive: true
            }];
        }

        const payload = {
            name: formData.name,
            description: formData.description,
            categoryId: parseInt(formData.categoryId),
            sportId: formData.sportId ? parseInt(formData.sportId) : 0, // 0 maps to null on C# backend
            basePrice: parseFloat(formData.basePrice),
            memberPrice: formData.memberPrice !== '' ? parseFloat(formData.memberPrice) : null,
            imageUrl: formData.imageUrl || null,
            isFeatured: formData.isFeatured,
            isActive: formData.isActive,
            isCustomizable: parseInt(formData.categoryId) === 2 ? true : formData.isCustomizable,
            variants: variantsPayload,
            images: (formData.images || []).map((img, idx) => ({
                id: img.id || null,
                imageUrl: img.imageUrl,
                sortOrder: idx
            })),
            kitItems: parseInt(formData.categoryId) === 2
                ? (formData.kitItems || []).map((ki, idx) => ({
                    kitPieceId: parseInt(ki.kitPieceId),
                    quantity: parseInt(ki.quantity) || 1,
                    sortOrder: idx
                }))
                : null
        };

        try {
            const url = editingProduct
                ? `${API_BASE_URL}/api/products/${editingProduct.id}`
                : `${API_BASE_URL}/api/products`;

            const method = editingProduct ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                addToast(editingProduct ? 'Artigo atualizado com sucesso!' : 'Artigo adicionado com sucesso!');
                setShowModal(false);
                resetForm();
                fetchProducts();
            } else {
                const errData = await response.json().catch(() => ({}));
                addToast(errData.message || 'Erro ao guardar artigo na loja', 'error');
            }
        } catch (error) {
            console.error('Submit error:', error);
            addToast('Erro técnico ao processar formulário', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    // Soft delete/Deactivate
    const handleDeleteClick = (id) => {
        setConfirmState({ isOpen: true, id });
    };

    const handleDeleteConfirm = async () => {
        const id = confirmState.id;
        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`${API_BASE_URL}/api/products/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                addToast('Artigo removido da loja com sucesso', 'warning');
                fetchProducts();
            } else {
                addToast('Erro ao remover o artigo', 'error');
            }
        } catch (error) {
            console.error('Delete error:', error);
            addToast('Erro de rede ao remover artigo', 'error');
        } finally {
            setConfirmState({ isOpen: false, id: null });
        }
    };

    const handleEditClick = async (product) => {
        // Fetch detailed product to load all variants properly
        try {
            const response = await fetch(`${API_BASE_URL}/api/products/${product.id}`);
            if (response.ok) {
                const detail = await response.json();
                setEditingProduct(detail);
                setFormData({
                    name: detail.name || '',
                    description: detail.description || '',
                    categoryId: detail.categoryId || '',
                    sportId: detail.sportId || '',
                    basePrice: detail.basePrice || '',
                    memberPrice: detail.memberPrice || '',
                    imageUrl: detail.imageUrl || '',
                    isFeatured: detail.isFeatured || false,
                    isActive: detail.isActive,
                    isCustomizable: parseInt(detail.categoryId) === 2 ? true : (detail.isCustomizable || false),
                    variants: detail.variants || [],
                    images: detail.images || [],
                    kitItems: (detail.kitItems || []).map(ki => ({
                        kitPieceId: ki.kitPieceId,
                        pieceName: ki.pieceName,
                        quantity: ki.quantity,
                        customizationType: ki.customizationType,
                        availableSizes: ki.availableSizes || [],
                        sortOrder: ki.sortOrder
                    }))
                });
                setShowModal(true);
            } else {
                addToast('Erro ao obter detalhes do artigo para edição', 'error');
            }
        } catch (error) {
            console.error('Edit load error:', error);
            addToast('Erro ao carregar artigo para editar', 'error');
        }
    };

    const handleDuplicateClick = async (product) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/products/${product.id}`);
            if (response.ok) {
                const detail = await response.json();
                setEditingProduct(null);
                setFormData({
                    name: `Cópia de ${detail.name}`,
                    description: detail.description || '',
                    categoryId: detail.categoryId || '',
                    sportId: detail.sportId || '',
                    basePrice: detail.basePrice || '',
                    memberPrice: detail.memberPrice || '',
                    imageUrl: detail.imageUrl || '',
                    isFeatured: false,
                    isActive: detail.isActive,
                    isCustomizable: parseInt(detail.categoryId) === 2 ? true : (detail.isCustomizable || false),
                    variants: (detail.variants || []).map(v => ({ ...v, id: null })),
                    images: (detail.images || []).map(img => ({ ...img, id: null })),
                    kitItems: (detail.kitItems || []).map(ki => ({
                        kitPieceId: ki.kitPieceId,
                        pieceName: ki.pieceName,
                        quantity: ki.quantity,
                        customizationType: ki.customizationType,
                        availableSizes: ki.availableSizes || [],
                        sortOrder: ki.sortOrder
                    }))
                });
                setShowModal(true);
                addToast('Artigo duplicado.', 'info');
            } else {
                addToast('Erro ao obter detalhes do artigo para duplicar', 'error');
            }
        } catch (error) {
            console.error('Duplicate load error:', error);
            addToast('Erro ao carregar artigo para duplicar', 'error');
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            categoryId: categories.length > 0 ? categories[0].id : '',
            sportId: '',
            basePrice: '',
            memberPrice: '',
            imageUrl: '',
            isFeatured: false,
            isActive: true,
            isCustomizable: false,
            variants: [],
            images: [],
            kitItems: [],
            singleStockQty: 0
        });
        setEditingProduct(null);
        setKitPiecesSelection({});
    };

    const getImageUrl = (url) => {
        if (!url) return null;
        if (url.startsWith('http')) return url;
        return `${API_BASE_URL}${url.startsWith('/') ? url : '/' + url}`;
    };

    const getTotalStock = (productVariants) => {
        if (!productVariants || productVariants.length === 0) return 0;
        return productVariants.reduce((sum, v) => sum + v.stockQty, 0);
    };

    const getStockBadge = (productVariants) => {
        const total = getTotalStock(productVariants);
        if (total === 0) {
            return <span className="stock-badge out-of-stock"><AlertTriangle size={12} /> Sem Stock</span>;
        }
        if (total <= 5) {
            return <span className="stock-badge low-stock">Stock Baixo ({total})</span>;
        }
        return <span className="stock-badge in-stock">Em Stock ({total})</span>;
    };

    const filteredOrders = orders.filter(order => {
        const query = (ordersSearch || '').toLowerCase().trim();
        if (!query) return true;

        const customerName = (order.customerName || '').toLowerCase();
        const customerEmail = (order.customerEmail || order.guestEmail || order.email || '').toLowerCase();
        const membershipNumber = (order.membershipNumber ? String(order.membershipNumber) : '').toLowerCase();
        const orderIdStr = String(order.id);

        return customerName.includes(query) ||
               customerEmail.includes(query) ||
               membershipNumber.includes(query) ||
               orderIdStr.includes(query);
    });

    const renderShippingAddress = (addressString) => {
        if (!addressString) {
            return (
                <span style={{
                    color: '#166534',
                    backgroundColor: '#dcfce7',
                    border: '1px solid #bbf7d0',
                    padding: '3px 9px',
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px'
                }}>
                    <i className="fas fa-store" /> Levantamento no Clube
                </span>
            );
        }
        try {
            const address = JSON.parse(addressString);
            return (
                <div className="shipping-address-details">
                    <p><strong>Rua:</strong> {address.Street || address.street || 'N/A'}</p>
                    <p><strong>Cidade:</strong> {address.City || address.city || 'N/A'}</p>
                    <p><strong>Código Postal:</strong> {address.PostalCode || address.postalCode || 'N/A'}</p>
                    <p><strong>País:</strong> {address.Country || address.country || 'Portugal'}</p>
                </div>
            );
        } catch (e) {
            return (
                <div className="shipping-address-details text-only">
                    <p>{addressString}</p>
                </div>
            );
        }
    };



    return (
        <div className="store-manager">
            {/* Toasts */}
            <div className="toast-container">
                {toasts.map(t => (
                    <Toast key={t.id} {...t} onClose={() => removeToast(t.id)} />
                ))}
            </div>

            {/* Confirm Dialog */}
            <ConfirmDialog
                isOpen={confirmState.isOpen}
                title="Desativar Artigo"
                message="Tens a certeza que desejas desativar este artigo? Ele deixará de estar visível para os utilizadores na Loja pública."
                onConfirm={handleDeleteConfirm}
                onCancel={() => setConfirmState({ isOpen: false, id: null })}
            />

            <header className="manager-header">
                <div className="header-content">
                    <h1>Gestão de Loja</h1>
                    <p className="header-subtitle">Gestão de equipamentos oficiais, merchandising e kits de produtos</p>
                </div>
                {activeTab === 'products' && (
                    <button className="btn-add" onClick={() => { resetForm(); setShowModal(true); }}>
                        <Plus size={20} /> Adicionar Artigo
                    </button>
                )}
                {activeTab === 'kit-pieces' && (
                    <button className="btn-add" onClick={() => { resetKitPieceForm(); setShowKitPieceModal(true); }}>
                        <Plus size={20} /> Nova Peça de Kit
                    </button>
                )}
            </header>

            <div className="manager-tabs">
                <button
                    className={`tab-button ${activeTab === 'products' ? 'active' : ''}`}
                    onClick={() => setActiveTab('products')}
                >
                    Gestão de Produtos
                </button>
                <button
                    className={`tab-button ${activeTab === 'orders' ? 'active' : ''}`}
                    onClick={() => setActiveTab('orders')}
                >
                    Gestão de Encomendas
                </button>
                <button
                    className={`tab-button ${activeTab === 'kit-pieces' ? 'active' : ''}`}
                    onClick={() => setActiveTab('kit-pieces')}
                >
                    <Layers size={16} style={{ marginRight: '6px', verticalAlign: '-2px' }} />
                    Peças de Kit
                </button>
            </div>

            {activeTab === 'products' ? (
                <>
                    {/* Filter controls */}
                    <div className="filters-row">
                        <div className="search-box">
                            <Search className="search-icon" size={18} />
                            <input
                                type="text"
                                placeholder="Pesquisar artigos..."
                                value={searchQuery}
                                onChange={handleSearchChange}
                            />
                        </div>
                        <select
                            className="filter-select"
                            value={selectedCategory}
                            onChange={handleCategoryFilterChange}
                        >
                            <option value="">Todas as Categorias</option>
                            {categories.map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                        <select
                            className="filter-select"
                            value={selectedSport}
                            onChange={handleSportFilterChange}
                        >
                            <option value="">Todas as Modalidades</option>
                            {sports.map(s => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Products table */}
                    {loading ? (
                        <div className="loading-container" style={{ padding: '60px', textAlign: 'center' }}>
                            <Loader2 className="spinner" size={40} style={{ margin: '0 auto 10px auto', display: 'block', animation: 'spin 1s linear infinite' }} />
                            <p>A sincronizar stock e artigos da loja...</p>
                        </div>
                    ) : products.length === 0 ? (
                        <div className="no-results empty-state glass-card" style={{ padding: '40px', textAlign: 'center' }}>
                            <ShoppingBag size={48} style={{ color: '#cbd5e1', marginBottom: '12px' }} />
                            <h3>Nenhum artigo encontrado</h3>
                            <p>Experimente ajustar os filtros ou adicione um novo artigo para começar.</p>
                        </div>
                    ) : (
                        <>
                            <div className="products-table-wrapper">
                                <table className="products-table">
                                    <thead>
                                        <tr>
                                            <th>Artigo</th>
                                            <th>Modalidade</th>
                                            <th>Preço Base / Sócio</th>
                                            <th>Destaque</th>
                                            <th>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {products.map((product) => (
                                            <tr key={product.id}>
                                                <td>
                                                    <div className="product-info-cell">
                                                        {product.imageUrl ? (
                                                            <img
                                                                src={getImageUrl(product.imageUrl)}
                                                                alt={product.name}
                                                                className="product-thumb"
                                                            />
                                                        ) : (
                                                            <div className="product-no-image">
                                                                <ShoppingBag size={20} />
                                                            </div>
                                                        )}
                                                        <div className="product-details">
                                                            <h4>{product.name}</h4>
                                                            <span className="product-category-tag">
                                                                {product.categoryName}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    {product.sportName ? (
                                                        <span className="badge-status active">{product.sportName}</span>
                                                    ) : (
                                                        <span className="badge-status inactive">Geral</span>
                                                    )}
                                                </td>
                                                <td className="price-cell">
                                                    <div>{product.basePrice != null ? product.basePrice.toFixed(2) : '0.00'}€</div>
                                                    {product.memberPrice != null && (
                                                        <span className="member-price-tag">
                                                            Sócio: {product.memberPrice.toFixed(2)}€
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    {product.isFeatured ? (
                                                        <span style={{ color: '#eab308', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                            <Star size={16} fill="#eab308" /> Destacado
                                                        </span>
                                                    ) : (
                                                        <span style={{ color: '#94a3b8' }}>Não</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <div className="store-action-buttons">
                                                        <button
                                                            className="btn-action edit"
                                                            onClick={() => handleEditClick(product)}
                                                            title="Editar Artigo"
                                                        >
                                                            <Pencil size={16} />
                                                        </button>
                                                        <button
                                                            className="btn-action edit"
                                                            onClick={() => handleDuplicateClick(product)}
                                                            title="Duplicar Artigo"
                                                        >
                                                            <Copy size={16} />
                                                        </button>
                                                        <button
                                                            className="btn-action delete"
                                                            onClick={() => handleDeleteClick(product.id)}
                                                            title="Desativar Artigo"
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="pagination-row">
                                    <span>Mostrando página {currentPage} de {totalPages} ({totalCount} artigos no total)</span>
                                    <div className="pagination-controls">
                                        <button
                                            className="btn-page"
                                            disabled={currentPage === 1}
                                            onClick={() => setCurrentPage(prev => prev - 1)}
                                        >
                                            <ChevronLeft size={18} />
                                        </button>
                                        <button
                                            className="btn-page"
                                            disabled={currentPage === totalPages}
                                            onClick={() => setCurrentPage(prev => prev + 1)}
                                        >
                                            <ChevronRight size={18} />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </>
            ) : activeTab === 'orders' ? (
                <>
                    {/* Orders Filter controls */}
                    <div className="filters-row">
                        <div className="search-box">
                            <Search className="search-icon" size={18} />
                            <input
                                type="text"
                                placeholder="Pesquisar por ID, Cliente, Email ou Nº Sócio..."
                                value={ordersSearch}
                                onChange={(e) => {
                                    setOrdersSearch(e.target.value);
                                    setOrdersCurrentPage(1);
                                }}
                            />
                            {ordersSearch && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setOrdersSearch('');
                                        setOrdersCurrentPage(1);
                                    }}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        color: '#94a3b8',
                                        padding: '4px',
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}
                                    title="Limpar pesquisa"
                                >
                                    <X size={14} />
                                </button>
                            )}
                        </div>
                        <select
                            className="filter-select"
                            value={ordersStatusFilter}
                            onChange={(e) => {
                                setOrdersStatusFilter(e.target.value);
                                setOrdersCurrentPage(1);
                                setSelectedOrderIds(new Set());
                            }}
                        >
                            <option value="">Todos os Estados</option>
                            <option value="Pendente">Pendente</option>
                            <option value="Pago">Pago</option>
                            <option value="EmProcessamento">Em Processamento</option>
                            <option value="Enviado">Enviado</option>
                            <option value="Entregue">Entregue</option>
                            <option value="Cancelado">Cancelado</option>
                        </select>
                    </div>

                    {/* Export toolbar */}
                    <div className="export-toolbar">
                        <div className="export-info">
                            {selectedOrderIds.size > 0 ? (
                                <span className="export-selection-badge">
                                    <CheckSquare size={14} />
                                    {selectedOrderIds.size} selecionada(s)
                                </span>
                            ) : (
                                <span className="export-hint">Seleciona encomendas ou exporta tudo</span>
                            )}
                        </div>
                        <div className="export-actions">
                            {selectedOrderIds.size > 0 && (
                                <button
                                    className="btn-export btn-export-selected"
                                    onClick={handleExportSelected}
                                    title="Exportar encomendas selecionadas"
                                >
                                    <FileSpreadsheet size={16} />
                                    Exportar Selecionadas ({selectedOrderIds.size})
                                </button>
                            )}
                            <button
                                className="btn-export btn-export-all"
                                onClick={handleExportAll}
                                disabled={exportingAll}
                                title="Exportar todas as encomendas para Excel"
                            >
                                {exportingAll ? (
                                    <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                                ) : (
                                    <Download size={16} />
                                )}
                                {exportingAll ? 'A exportar...' : 'Exportar Todas'}
                            </button>
                        </div>
                    </div>

                    {/* Orders table */}
                    {ordersLoading ? (
                        <div className="loading-container" style={{ padding: '60px', textAlign: 'center' }}>
                            <Loader2 className="spinner" size={40} style={{ margin: '0 auto 10px auto', display: 'block', animation: 'spin 1s linear infinite' }} />
                            <p>A carregar encomendas...</p>
                        </div>
                    ) : filteredOrders.length === 0 ? (
                        <div className="no-results empty-state glass-card" style={{ padding: '40px', textAlign: 'center' }}>
                            <ShoppingBag size={48} style={{ color: '#cbd5e1', marginBottom: '12px' }} />
                            <h3>Nenhuma encomenda encontrada</h3>
                            <p>Experimente ajustar os filtros ou aguarde por novas encomendas na loja.</p>
                        </div>
                    ) : (
                        <>
                            <div className="products-table-wrapper">
                                <table className="products-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: '40px' }}>
                                                <input
                                                    type="checkbox"
                                                    className="order-checkbox"
                                                    checked={filteredOrders.length > 0 && selectedOrderIds.size === filteredOrders.length}
                                                    onChange={toggleSelectAll}
                                                    title="Selecionar / desselecionar tudo"
                                                />
                                            </th>
                                            <th>Nº</th>
                                            <th>Cliente</th>
                                            <th>Data</th>
                                            <th>Artigos</th>
                                            <th>Valor Total</th>
                                            <th>Estado</th>
                                            <th>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredOrders.map((order) => (
                                            <tr
                                                key={order.id}
                                                className={selectedOrderIds.has(order.id) ? 'order-row-selected' : ''}
                                            >
                                                <td>
                                                    <input
                                                        type="checkbox"
                                                        className="order-checkbox"
                                                        checked={selectedOrderIds.has(order.id)}
                                                        onChange={() => toggleOrderSelect(order.id)}
                                                    />
                                                </td>
                                                <td><strong>#{order.id}</strong></td>
                                                <td>
                                                    <div>
                                                        <strong>{order.customerName || 'Cliente Convidado'}</strong>
                                                        {(order.customerEmail || order.membershipNumber) && (
                                                            <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '3px', display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                                                                {order.membershipNumber && (
                                                                    <span style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: '4px', fontWeight: 700, color: '#003380', fontSize: '0.75rem', border: '1px solid #cbd5e1' }}>
                                                                        Sócio #{order.membershipNumber}
                                                                    </span>
                                                                )}
                                                                {order.customerEmail && (
                                                                    <span style={{ color: '#475569' }}>{stripEmailAlias(order.customerEmail)}</span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>{parseUtcDate(order.createdAt).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                                                <td>{order.itemCount} artigo(s)</td>
                                                <td className="price-cell">{(order.totalAmount || 0).toFixed(2)}€</td>
                                                <td>
                                                    <span className={`badge-order-status ${order.status.toLowerCase()}`}>
                                                        {order.status}
                                                    </span>
                                                </td>
                                                <td>
                                                        <div className="store-action-buttons">
                                                            {order.moloniDocumentId ? (
                                                                <button
                                                                    className="btn-action invoice-view"
                                                                    onClick={() => handleViewInvoice(order.id)}
                                                                    title="Ver Fatura"
                                                                    style={{ backgroundColor: '#3b82f6', color: 'white' }}
                                                                >
                                                                    <Receipt size={16} />
                                                                </button>
                                                            ) : (
                                                                <button
                                                                    className="btn-action invoice"
                                                                    onClick={() => handleGenerateInvoice(order.id)}
                                                                    disabled={generatingInvoiceId === order.id}
                                                                    title="Gerar Fatura no Moloni"
                                                                    style={{ backgroundColor: '#10b981', color: 'white' }}
                                                                >
                                                                    {generatingInvoiceId === order.id ? (
                                                                        <Loader2 size={16} className="spinner" />
                                                                    ) : (
                                                                        <Receipt size={16} />
                                                                    )}
                                                                </button>
                                                            )}
                                                            <button
                                                                className="btn-action edit"
                                                                onClick={() => fetchOrderDetails(order.id)}
                                                                title="Ver Detalhes"
                                                            >
                                                                <Eye size={16} />
                                                            </button>
                                                            <button
                                                                className="btn-action export"
                                                                onClick={() => handleExportSingle(order)}
                                                                title="Exportar encomenda para Excel"
                                                            >
                                                                <Download size={16} />
                                                            </button>
                                                        </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Orders Pagination */}
                            {ordersTotalPages > 1 && (
                                <div className="pagination-row">
                                    <span>Mostrando página {ordersCurrentPage} de {ordersTotalPages} ({ordersTotalCount} encomendas no total)</span>
                                    <div className="pagination-controls">
                                        <button
                                            className="btn-page"
                                            disabled={ordersCurrentPage === 1}
                                            onClick={() => setOrdersCurrentPage(prev => prev - 1)}
                                        >
                                            <ChevronLeft size={18} />
                                        </button>
                                        <button
                                            className="btn-page"
                                            disabled={ordersCurrentPage === ordersTotalPages}
                                            onClick={() => setOrdersCurrentPage(prev => prev + 1)}
                                        >
                                            <ChevronRight size={18} />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </>
            ) : activeTab === 'kit-pieces' ? (
                <>
                    <div className="filters-row">
                        <div className="search-box">
                            <Search className="search-icon" size={18} />
                            <input
                                type="text"
                                placeholder="Pesquisar peças..."
                                value={kitPieceSearch}
                                onChange={(e) => setKitPieceSearch(e.target.value)}
                            />
                        </div>
                        <select
                            className="filter-select"
                            value={kitPieceSportFilter}
                            onChange={(e) => setKitPieceSportFilter(e.target.value)}
                        >
                            <option value="">Todas as Modalidades</option>
                            {sports.map(s => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                        </select>
                    </div>

                    {kitPiecesLoading ? (
                        <div className="loading-container" style={{ padding: '60px', textAlign: 'center' }}>
                            <Loader2 className="spinner" size={40} style={{ margin: '0 auto 10px auto', display: 'block', animation: 'spin 1s linear infinite' }} />
                            <p>A carregar peças de kit...</p>
                        </div>
                    ) : kitPieces.filter(kp => kp.name.toLowerCase().includes(kitPieceSearch.toLowerCase())).length === 0 ? (
                        <div className="no-results empty-state glass-card" style={{ padding: '40px', textAlign: 'center' }}>
                            <Layers size={48} style={{ color: '#cbd5e1', marginBottom: '12px' }} />
                            <h3>Nenhuma peça encontrada</h3>
                            <p>Experimente ajustar os filtros ou adicione uma nova peça.</p>
                        </div>
                    ) : (
                        <div className="products-table-wrapper">
                            <table className="products-table">
                                <thead>
                                    <tr>
                                        <th>Nome da Peça</th>
                                        <th>Modalidade</th>
                                        <th>Tamanhos</th>
                                        <th>Personalização</th>
                                        <th>Estado</th>
                                        <th>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {kitPieces.filter(kp => kp.name.toLowerCase().includes(kitPieceSearch.toLowerCase()) && (!kitPieceSportFilter || kp.sportId === parseInt(kitPieceSportFilter))).map((piece) => (
                                        <tr key={piece.id}>
                                            <td><strong>{piece.name}</strong></td>
                                            <td>{piece.sportName || '-'}</td>
                                            <td>
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                    {(piece.availableSizes || []).map(s => (
                                                        <span key={s} style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px', fontSize: '11px', color: '#475569', fontWeight: 600 }}>
                                                            {s}
                                                        </span>
                                                    ))}
                                                </div>
                                            </td>
                                            <td>
                                                {piece.customizationType && piece.customizationType !== 'None' ? (
                                                    <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        <CheckSquare size={16} /> {piece.customizationType === 'NameAndNumber' ? 'Nome e Número' : 'Apenas Número'}
                                                    </span>
                                                ) : (
                                                    <span style={{ color: '#94a3b8' }}>Não</span>
                                                )}
                                            </td>
                                            <td>
                                                {piece.isActive ? (
                                                    <span className="badge-status active">Ativo</span>
                                                ) : (
                                                    <span className="badge-status inactive">Inativo</span>
                                                )}
                                            </td>
                                            <td>
                                                <div className="store-action-buttons">
                                                    <button
                                                        className="btn-action edit"
                                                        onClick={() => handleEditKitPiece(piece)}
                                                        title="Editar Peça"
                                                    >
                                                        <Pencil size={16} />
                                                    </button>
                                                    <button
                                                        className="btn-action edit"
                                                        onClick={() => handleDuplicateKitPiece(piece)}
                                                        title="Duplicar Peça"
                                                    >
                                                        <Copy size={16} />
                                                    </button>
                                                    <button
                                                        className="btn-action delete"
                                                        onClick={() => handleToggleKitPieceActive(piece)}
                                                        title={piece.isActive ? 'Desativar Peça' : 'Reativar Peça'}
                                                    >
                                                        {piece.isActive ? <EyeOff size={16} /> : <Eye size={16} />}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            ) : null}

            {/* Modal for Kit Pieces */}
            {showKitPieceModal && (
                <div className="modal-overlay" onClick={() => setShowKitPieceModal(false)}>
                    <div className="modal-container" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
                        <div className="modal-header">
                            <h2>{editingKitPiece ? 'Editar Peça de Kit' : 'Nova Peça de Kit'}</h2>
                            <button className="btn-close" onClick={() => setShowKitPieceModal(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleSaveKitPiece}>
                            <div className="modal-body">
                                <div className="form-group full-width">
                                    <label>Nome da Peça *</label>
                                    <input
                                        type="text"
                                        value={kitPieceForm.name}
                                        onChange={e => setKitPieceForm({ ...kitPieceForm, name: e.target.value })}
                                        required
                                        placeholder="Ex: Camisola de Jogo"
                                    />
                                </div>
                                <div className="form-group full-width">
                                    <label>Modalidade *</label>
                                    <select
                                        value={kitPieceForm.sportId}
                                        onChange={e => setKitPieceForm({ ...kitPieceForm, sportId: e.target.value })}
                                    >
                                        <option value="">Geral / Sem Modalidade específica</option>
                                        {sports.map(s => (
                                            <option key={s.id} value={s.id}>{s.name}</option>
                                        ))}
                                    </select>
                                    <span style={{ fontSize: '11px', color: '#64748b', marginTop: '4px', display: 'block' }}>
                                        As peças de kit são específicas por modalidade (ex: A Camisola de Futsal é diferente da do Basquetebol).
                                    </span>
                                </div>

                                <div className="form-group full-width" style={{ marginTop: '16px', marginBottom: '20px' }}>
                                    <label>Personalização *</label>
                                    <select
                                        value={kitPieceForm.customizationType}
                                        onChange={e => setKitPieceForm({ ...kitPieceForm, customizationType: e.target.value })}
                                    >
                                        <option value="None">Nenhuma Personalização</option>
                                        <option value="NameAndNumber">Nome e Número</option>
                                        <option value="NumberOnly">Apenas Número</option>
                                    </select>
                                </div>

                                <div className="form-group full-width">
                                    <label>Tamanhos Disponíveis *</label>
                                    <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                                        <input
                                            type="text"
                                            value={newSizeInput}
                                            onChange={e => setNewSizeInput(e.target.value)}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    addKitPieceSize();
                                                }
                                            }}
                                            placeholder="Adicionar tamanho (Ex: XL ou 39-41)"
                                            style={{ flex: 1 }}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => addKitPieceSize()}
                                            style={{ background: 'var(--color-primary)', color: '#fff', border: 'none', borderRadius: '6px', padding: '0 16px', fontWeight: 600, cursor: 'pointer' }}
                                        >
                                            Adicionar
                                        </button>
                                    </div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
                                        {kitPieceForm.availableSizes.map(size => (
                                            <div key={size} style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '4px 8px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>
                                                {size}
                                                <button type="button" onClick={() => removeKitPieceSize(size)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '2px', display: 'flex' }}>
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                        <button type="button" onClick={() => applySizePreset(['XS', 'S', 'M', 'L', 'XL', 'XXL'])} style={{ fontSize: '11px', background: '#f8fafc', border: '1px solid #e2e8f0', padding: '4px 10px', borderRadius: '999px', cursor: 'pointer', color: '#475569' }}>Adulto (XS-XXL)</button>
                                        <button type="button" onClick={() => applySizePreset(['4', '6', '8', '10', '12', '14', '16'])} style={{ fontSize: '11px', background: '#f8fafc', border: '1px solid #e2e8f0', padding: '4px 10px', borderRadius: '999px', cursor: 'pointer', color: '#475569' }}>Criança (4-16)</button>
                                        <button type="button" onClick={() => applySizePreset(['35-38', '39-42', '43-46'])} style={{ fontSize: '11px', background: '#f8fafc', border: '1px solid #e2e8f0', padding: '4px 10px', borderRadius: '999px', cursor: 'pointer', color: '#475569' }}>Meias (35-46)</button>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn-cancel" onClick={() => setShowKitPieceModal(false)} disabled={submittingKitPiece}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-save" disabled={submittingKitPiece}>
                                    {submittingKitPiece ? <Loader2 size={18} className="spinner" /> : <Save size={18} />}
                                    {editingKitPiece ? 'Atualizar Peça' : 'Criar Peça'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal for Add / Edit */}
            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal-container" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingProduct ? 'Editar Artigo' : 'Adicionar Artigo'}</h2>
                            <button className="btn-close" onClick={() => setShowModal(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="form-grid">
                                    <div className="form-group full-width">
                                        <label>Nome do Artigo *</label>
                                        <input
                                            type="text"
                                            value={formData.name}
                                            onChange={e => setFormData({ ...formData, name: e.target.value })}
                                            required
                                            placeholder="Ex: Camisola Oficial CDP 2026/2027"
                                        />
                                    </div>
                                    <div className="form-group full-width">
                                        <label>Descrição</label>
                                        <textarea
                                            value={formData.description}
                                            onChange={e => setFormData({ ...formData, description: e.target.value })}
                                            placeholder="Ex: Camisola de alta performance com materiais reciclados..."
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Categoria *</label>
                                        <select
                                            value={formData.categoryId}
                                            onChange={e => {
                                                const newCatId = e.target.value;
                                                const updates = {
                                                    categoryId: newCatId,
                                                    isCustomizable: parseInt(newCatId) === 2 ? true : formData.isCustomizable
                                                };
                                                if (parseInt(formData.categoryId) === 2 && parseInt(newCatId) !== 2 && formData.kitItems && formData.kitItems.length > 0) {
                                                    if (window.confirm('Mudar a categoria vai remover as peças de kit que já adicionaste. Queres continuar?')) {
                                                        updates.kitItems = [];
                                                        setFormData({ ...formData, ...updates });
                                                    }
                                                } else {
                                                    setFormData({ ...formData, ...updates });
                                                }
                                            }}
                                            required
                                        >
                                            <option value="">Selecionar Categoria</option>
                                            {categories.map(c => (
                                                <option key={c.id} value={c.id}>{c.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Modalidade (Desporto)</label>
                                        <select
                                            value={formData.sportId}
                                            onChange={e => {
                                                const newSportId = e.target.value;
                                                const currentSportId = formData.sportId ? formData.sportId.toString() : '';
                                                if (parseInt(formData.categoryId) === 2 && formData.kitItems && formData.kitItems.length > 0 && currentSportId !== newSportId) {
                                                    if (window.confirm('Mudar a modalidade vai remover as peças de kit que já adicionaste. Queres continuar?')) {
                                                        setFormData({ ...formData, sportId: newSportId, kitItems: [] });
                                                    }
                                                } else {
                                                    setFormData({ ...formData, sportId: newSportId });
                                                }
                                            }}
                                        >
                                            <option value="">Geral / Sem Modalidade específica</option>
                                            {sports.map(s => (
                                                <option key={s.id} value={s.id}>{s.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Preço Base (€) *</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={formData.basePrice}
                                            onChange={e => setFormData({ ...formData, basePrice: e.target.value })}
                                            required
                                            placeholder="0.00"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Preço Sócio (€)</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={formData.memberPrice}
                                            onChange={e => setFormData({ ...formData, memberPrice: e.target.value })}
                                            placeholder="Deixar em branco para igual ao preço base"
                                        />
                                    </div>

                                    <div className="form-group full-width">
                                        <label>Imagem do Artigo</label>
                                        <div className="image-upload-zone">
                                            {uploadingImage ? (
                                                <div className="upload-loading">
                                                    <Loader2 className="spinner" size={24} style={{ animation: 'spin 1s linear infinite' }} />
                                                    <span>A carregar imagem...</span>
                                                </div>
                                            ) : formData.imageUrl ? (
                                                <div className="image-preview-container">
                                                    <img src={getImageUrl(formData.imageUrl)} alt="Preview" className="image-upload-preview" />
                                                    <button type="button" className="btn-remove-image" onClick={() => setFormData({ ...formData, imageUrl: '' })}>
                                                        <X size={16} /> Remover
                                                    </button>
                                                </div>
                                            ) : (
                                                <label className="upload-placeholder">
                                                    <Plus size={24} style={{ marginBottom: '6px', color: 'var(--color-primary)' }} />
                                                    <span>Clique para carregar uma imagem</span>
                                                    <span className="upload-specs">(Formatos aceites: JPG, PNG, WEBP. Max 2MB)</span>
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={handleImageUpload}
                                                        style={{ display: 'none' }}
                                                    />
                                                </label>
                                            )}
                                        </div>
                                    </div>

                                    <div className="form-group full-width">
                                        <label>Imagens Adicionais (Galeria)</label>
                                        <div className="gallery-upload-grid">
                                            {formData.images && formData.images.map((img, index) => (
                                                <div key={index} className="gallery-preview-container">
                                                    <img src={getImageUrl(img.imageUrl)} alt={`Galeria ${index}`} className="gallery-upload-preview" />
                                                    <button type="button" className="btn-remove-gallery-image" onClick={() => removeGalleryImage(index)}>
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                            ))}
                                            {uploadingGalleryImage ? (
                                                <div className="gallery-upload-placeholder loading">
                                                    <Loader2 className="spinner" size={18} style={{ animation: 'spin 1s linear infinite' }} />
                                                </div>
                                            ) : (
                                                <label className="gallery-upload-placeholder clickable">
                                                    <Plus size={20} />
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={handleGalleryImageUpload}
                                                        style={{ display: 'none' }}
                                                    />
                                                </label>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="checkbox-row">
                                    <label className="checkbox-group">
                                        <input
                                            type="checkbox"
                                            checked={formData.isFeatured}
                                            onChange={e => setFormData({ ...formData, isFeatured: e.target.checked })}
                                        />
                                        <span>Destacar na Loja</span>
                                    </label>
                                    {[1, 2].includes(parseInt(formData.categoryId)) && (
                                        <label className="checkbox-group" style={parseInt(formData.categoryId) === 2 ? { opacity: 0.8, cursor: 'not-allowed' } : {}}>
                                            <input
                                                type="checkbox"
                                                checked={parseInt(formData.categoryId) === 2 ? true : formData.isCustomizable}
                                                disabled={parseInt(formData.categoryId) === 2}
                                                onChange={e => setFormData({ ...formData, isCustomizable: e.target.checked })}
                                            />
                                            <span>
                                                Permite Personalização (Nome/Nº)
                                                {parseInt(formData.categoryId) === 2 && <span style={{ fontSize: '0.85em', color: '#64748b', marginLeft: '6px' }}>(Obrigatória para Kits)</span>}
                                            </span>
                                        </label>
                                    )}
                                    {editingProduct && (
                                        <label className="checkbox-group">
                                            <input
                                                type="checkbox"
                                                checked={formData.isActive}
                                                onChange={e => setFormData({ ...formData, isActive: e.target.checked })}
                                            />
                                            <span>Atigo Ativo</span>
                                        </label>
                                    )}
                                </div>

                                {/* Kit Composition section for Kits */}
                                {parseInt(formData.categoryId) === 2 ? (
                                    <div className="kit-composition-section" style={{
                                        marginTop: '20px',
                                        padding: '16px',
                                        background: '#f8fafc',
                                        borderRadius: '12px',
                                        border: '1px solid #e2e8f0'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                            <div>
                                                <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
                                                    <Layers size={18} color="var(--color-primary)" /> Composição do Kit (Peças de Roupa)
                                                </h3>
                                                <p style={{ fontSize: '12px', color: '#64748b', margin: '4px 0 0 0' }}>
                                                    Adicione as peças de roupa que compõem este kit. O atleta escolherá o tamanho de cada peça individualmente.
                                                </p>
                                            </div>
                                        </div>

                                        {!formData.sportId ? (
                                            <div style={{ padding: '12px', background: '#fffbeb', borderRadius: '8px', border: '1px solid #fef3c7', color: '#92400e', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <AlertTriangle size={16} />
                                                <span>Selecione primeiro uma <strong>Modalidade</strong> acima para carregar as peças disponíveis.</span>
                                            </div>
                                        ) : (
                                            <>
                                                {/* Kit pieces selection list */}
                                                <div style={{ background: '#ffffff', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                                        <label style={{ fontSize: '13px', fontWeight: 600, color: '#475569' }}>
                                                            Peças Disponíveis
                                                        </label>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const availablePieces = kitPieces.filter(kp => kp.isActive && (!kp.sportId || kp.sportId === parseInt(formData.sportId)) && !(formData.kitItems || []).some(ki => ki.kitPieceId === kp.id));
                                                                const allSelected = availablePieces.every(p => kitPiecesSelection[p.id]);
                                                                if (allSelected) {
                                                                    setKitPiecesSelection({});
                                                                } else {
                                                                    const newSelection = {};
                                                                    availablePieces.forEach(p => newSelection[p.id] = 1);
                                                                    setKitPiecesSelection(newSelection);
                                                                }
                                                            }}
                                                            style={{ fontSize: '12px', background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontWeight: 500 }}
                                                        >
                                                            Selecionar Tudo
                                                        </button>
                                                    </div>

                                                    <div style={{ maxHeight: '250px', overflowY: 'auto', border: '1px solid #cbd5e1', borderRadius: '6px' }}>
                                                        {kitPieces
                                                            .filter(kp => kp.isActive && (!kp.sportId || kp.sportId === parseInt(formData.sportId)) && !(formData.kitItems || []).some(ki => ki.kitPieceId === kp.id))
                                                            .map(kp => (
                                                                <div
                                                                    key={kp.id}
                                                                    onClick={() => {
                                                                        setKitPiecesSelection(prev => {
                                                                            if (prev[kp.id]) {
                                                                                const copy = { ...prev };
                                                                                delete copy[kp.id];
                                                                                return copy;
                                                                            } else {
                                                                                return { ...prev, [kp.id]: 1 };
                                                                            }
                                                                        });
                                                                    }}
                                                                    style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid #e2e8f0', background: kitPiecesSelection[kp.id] ? '#eff6ff' : '#fff', cursor: 'pointer' }}
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={!!kitPiecesSelection[kp.id]}
                                                                        readOnly
                                                                        style={{ marginRight: '12px', cursor: 'pointer' }}
                                                                    />
                                                                    <span style={{ flex: 1, fontSize: '13px', color: '#334155' }}>
                                                                        {kp.name} {kp.customizationType && kp.customizationType !== 'None' ? (kp.customizationType === 'NumberOnly' ? '(apenas Nº)' : '(Nome/Nº)') : ''} — [{kp.availableSizes.join(', ')}]
                                                                    </span>
                                                                    {kitPiecesSelection[kp.id] && (
                                                                        <div
                                                                            onClick={e => e.stopPropagation()}
                                                                            style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '12px' }}
                                                                        >
                                                                            <span style={{ fontSize: '11px', color: '#64748b' }}>Qtd:</span>
                                                                            <input
                                                                                type="number"
                                                                                min="1"
                                                                                max="20"
                                                                                value={kitPiecesSelection[kp.id]}
                                                                                onChange={e => {
                                                                                    const val = Math.max(1, parseInt(e.target.value) || 1);
                                                                                    setKitPiecesSelection(prev => ({ ...prev, [kp.id]: val }));
                                                                                }}
                                                                                style={{ width: '60px', padding: '4px 8px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '12px', textAlign: 'center' }}
                                                                            />
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ))
                                                        }
                                                        {kitPieces.filter(kp => kp.isActive && (!kp.sportId || kp.sportId === parseInt(formData.sportId)) && !(formData.kitItems || []).some(ki => ki.kitPieceId === kp.id)).length === 0 && (
                                                            <div style={{ padding: '16px', textAlign: 'center', fontSize: '13px', color: '#64748b' }}>
                                                                Nenhuma peça disponível para adicionar.
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
                                                        <button
                                                            type="button"
                                                            onClick={handleAddPieceToProduct}
                                                            style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'var(--color-primary)', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                                                        >
                                                            <Plus size={16} />
                                                            Adicionar Peças Selecionadas
                                                        </button>
                                                    </div>
                                                </div>

                                                {kitPieces.filter(kp => kp.sportId === parseInt(formData.sportId)).length === 0 && (
                                                    <div style={{ padding: '10px 14px', background: '#f1f5f9', borderRadius: '8px', color: '#475569', fontSize: '12px', marginBottom: '12px' }}>
                                                        Ainda não existem peças criadas para esta modalidade. Crie peças na aba <strong>"Peças de Kit"</strong>.
                                                    </div>
                                                )}

                                                {/* List of kit items */}
                                                {(!formData.kitItems || formData.kitItems.length === 0) ? (
                                                    <div style={{ textAlign: 'center', padding: '24px', background: '#fff', borderRadius: '8px', border: '1px dashed #cbd5e1', color: '#94a3b8', fontSize: '13px' }}>
                                                        <Package size={28} style={{ display: 'block', margin: '0 auto 8px auto', opacity: 0.5 }} />
                                                        Nenhuma peça adicionada a este kit ainda. Selecione as peças acima.
                                                    </div>
                                                ) : (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                        {formData.kitItems.map((item, idx) => (
                                                            <div
                                                                key={idx}
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'space-between',
                                                                    background: '#ffffff',
                                                                    padding: '10px 14px',
                                                                    borderRadius: '8px',
                                                                    border: '1px solid #e2e8f0',
                                                                    boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                                                                }}
                                                            >
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                    <span style={{
                                                                        width: '24px',
                                                                        height: '24px',
                                                                        borderRadius: '50%',
                                                                        background: '#f1f5f9',
                                                                        color: '#64748b',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        fontSize: '11px',
                                                                        fontWeight: 700
                                                                    }}>
                                                                        {idx + 1}
                                                                    </span>
                                                                    <div>
                                                                        <div style={{ fontWeight: 600, fontSize: '14px', color: '#0f172a' }}>
                                                                            {item.pieceName}
                                                                            {item.customizationType && item.customizationType !== 'None' && (
                                                                                <span style={{ marginLeft: '8px', fontSize: '11px', background: '#dbeafe', color: '#1e40af', padding: '2px 6px', borderRadius: '4px', fontWeight: 600 }}>
                                                                                    {item.customizationType === 'NumberOnly' ? 'Nº' : 'Nome/Nº'}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                                                                            Tamanhos: {(item.availableSizes || []).join(', ')}
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                                    {/* Qty control */}
                                                                    <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #cbd5e1', borderRadius: '6px', overflow: 'hidden' }}>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => updateProductKitItemQty(idx, -1)}
                                                                            style={{ border: 'none', background: '#f8fafc', padding: '4px 10px', cursor: 'pointer', fontWeight: 700, fontSize: '14px', color: '#475569' }}
                                                                        >
                                                                            -
                                                                        </button>
                                                                        <span style={{ padding: '4px 10px', fontSize: '13px', fontWeight: 700, minWidth: '30px', textAlign: 'center', background: '#fff' }}>
                                                                            {item.quantity}x
                                                                        </span>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => updateProductKitItemQty(idx, 1)}
                                                                            style={{ border: 'none', background: '#f8fafc', padding: '4px 10px', cursor: 'pointer', fontWeight: 700, fontSize: '14px', color: '#475569' }}
                                                                        >
                                                                            +
                                                                        </button>
                                                                    </div>

                                                                    {/* Reorder buttons */}
                                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                                        <button
                                                                            type="button"
                                                                            disabled={idx === 0}
                                                                            onClick={() => moveProductKitItem(idx, -1)}
                                                                            style={{ border: 'none', background: 'transparent', padding: '2px', cursor: idx === 0 ? 'default' : 'pointer', color: idx === 0 ? '#cbd5e1' : '#64748b' }}
                                                                            title="Mover para cima"
                                                                        >
                                                                            <ArrowUp size={14} />
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            disabled={idx === formData.kitItems.length - 1}
                                                                            onClick={() => moveProductKitItem(idx, 1)}
                                                                            style={{ border: 'none', background: 'transparent', padding: '2px', cursor: idx === formData.kitItems.length - 1 ? 'default' : 'pointer', color: idx === formData.kitItems.length - 1 ? '#cbd5e1' : '#64748b' }}
                                                                            title="Mover para baixo"
                                                                        >
                                                                            <ArrowDown size={14} />
                                                                        </button>
                                                                    </div>

                                                                    {/* Remove button */}
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => removeProductKitItem(idx)}
                                                                        style={{ border: 'none', background: 'transparent', color: '#ef4444', padding: '6px', cursor: 'pointer', borderRadius: '4px' }}
                                                                        title="Remover peça do kit"
                                                                    >
                                                                        <Trash2 size={16} />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                ) : (
                                    /* Variants Sub-section for Equipamentos and Merchandising */
                                    [1, 3].includes(parseInt(formData.categoryId)) && (
                                        <div className="variants-section">
                                            <div className="variants-header">
                                                <h3>Variantes (Tamanho, Cor, etc.)</h3>
                                                <div style={{ display: 'flex', gap: '8px' }}>
                                                    <button
                                                        type="button"
                                                        className="btn-add-variant"
                                                        style={{ borderStyle: 'solid', background: '#f1f5f9', color: '#475569', borderColor: '#cbd5e1' }}
                                                        onClick={prefillStandardSizes}
                                                    >
                                                        Tamanhos Padrão (XS-XXL)
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn-add-variant"
                                                        onClick={addVariantRow}
                                                    >
                                                        <Plus size={16} /> Adicionar Variante
                                                    </button>
                                                </div>
                                            </div>

                                            {formData.variants.length === 0 ? (
                                                <p style={{ fontSize: '13px', color: '#94a3b8', margin: '10px 0', fontStyle: 'italic' }}>
                                                    Nenhuma variante configurada. Use o botão acima para carregar tamanhos padrão ou adicione manualmente.
                                                </p>
                                            ) : (
                                                <>
                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '8px', marginBottom: '6px', padding: '0 4px' }}>
                                                        <span style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Tipo</span>
                                                        <span style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Valor</span>
                                                        <span></span>
                                                    </div>
                                                    {formData.variants.map((variant, index) => {
                                                        // Custom mode: variantType is set but not in the preset list
                                                        const isCustomType = variant.variantType === '__custom__' ||
                                                            (variant.variantType && !VARIANT_TYPE_OPTIONS.includes(variant.variantType));
                                                        return (
                                                            <div key={index} className="variant-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '8px', alignItems: 'center' }}>
                                                                {/* Tipo: select + campo custom */}
                                                                <div style={{ display: 'flex', gap: '4px', flexDirection: 'column' }}>
                                                                    <select
                                                                        value={isCustomType ? '__custom__' : (variant.variantType || 'Tamanho')}
                                                                        onChange={e => {
                                                                            if (e.target.value === '__custom__') {
                                                                                // Set sentinel so isCustomType becomes true and input appears
                                                                                handleVariantChange(index, 'variantType', '__custom__');
                                                                            } else {
                                                                                handleVariantChange(index, 'variantType', e.target.value);
                                                                            }
                                                                        }}
                                                                        style={{ padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', background: '#fff' }}
                                                                    >
                                                                        {VARIANT_TYPE_OPTIONS.map(opt => (
                                                                            <option key={opt} value={opt}>{opt}</option>
                                                                        ))}
                                                                        <option value="__custom__">Personalizado…</option>
                                                                    </select>
                                                                    {isCustomType && (
                                                                        <input
                                                                            type="text"
                                                                            autoFocus
                                                                            placeholder="Nome do tipo (ex: Material)"
                                                                            // Show empty when sentinel, otherwise show actual value
                                                                            value={variant.variantType === '__custom__' ? '' : variant.variantType}
                                                                            onChange={e => {
                                                                                // Save the typed value (or revert to sentinel if cleared)
                                                                                const val = e.target.value;
                                                                                handleVariantChange(index, 'variantType', val || '__custom__');
                                                                            }}
                                                                            style={{ padding: '6px 8px', borderRadius: '6px', border: '1px solid #3b82f6', fontSize: '12px', outline: 'none', marginTop: '2px' }}
                                                                        />
                                                                    )}
                                                                </div>
                                                                {/* Valor */}
                                                                <input
                                                                    type="text"
                                                                    placeholder={`Valor (ex: ${variant.variantType === 'Cor' ? 'Azul' : 'M'})`}
                                                                    value={variant.size || ''}
                                                                    onChange={e => handleVariantChange(index, 'size', e.target.value)}
                                                                    required
                                                                    style={{ padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px' }}
                                                                />
                                                                <button
                                                                    type="button"
                                                                    className="btn-remove-variant"
                                                                    onClick={() => removeVariantRow(index)}
                                                                >
                                                                    <Trash2 size={16} />
                                                                </button>
                                                            </div>
                                                        );
                                                    })}
                                                </>
                                            )}
                                        </div>
                                    )
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-save" disabled={submitting}>
                                    {submitting ? (
                                        <>
                                            <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> A Guardar...
                                        </>
                                    ) : (
                                        <>
                                            <Save size={18} /> Guardar
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal for Order Details */}
            {showOrderModal && selectedOrder && (
                <div className="modal-overlay" onClick={() => { setShowOrderModal(false); setSelectedOrder(null); }}>
                    <div className="modal-container" style={{ maxWidth: '750px' }} onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Detalhes da Encomenda #{selectedOrder.id}</h2>
                            <button className="btn-close" onClick={() => { setShowOrderModal(false); setSelectedOrder(null); }}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="modal-body" style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
                            {/* Customer Info Section */}
                            <div className="order-detail-section">
                                <h3>Informação do Cliente</h3>
                                <div className="order-info-grid">
                                    <div className="info-item">
                                        <label>Nome:</label>
                                        <span>{selectedOrder.guestName || 'N/A'}</span>
                                    </div>
                                    <div className="info-item">
                                        <label>Email:</label>
                                        <span>{stripEmailAlias(selectedOrder.guestEmail) || 'N/A'}</span>
                                    </div>
                                    <div className="info-item">
                                        <label>Data da Encomenda:</label>
                                        <span>{parseUtcDate(selectedOrder.createdAt).toLocaleString('pt-PT')}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Payment Method Section */}
                            {selectedOrder.paymentMethod && (
                                <div className="order-detail-section">
                                    <h3>Método de Pagamento</h3>
                                    <PaymentMethodCard method={selectedOrder.paymentMethod} amount={selectedOrder.totalAmount} />
                                </div>
                            )}

                            {/* Shipping Address Section */}
                            <div className="order-detail-section">
                                <h3>Morada de Envio</h3>
                                {renderShippingAddress(selectedOrder.shippingAddress)}
                                {selectedOrder.notes && (
                                    <div className="info-item" style={{ marginTop: '10px' }}>
                                        <label>Notas adicionais:</label>
                                        <span style={{ fontStyle: 'italic', background: '#f8fafc', padding: '8px', borderRadius: '6px', display: 'block', border: '1px solid #e2e8f0' }}>
                                            {selectedOrder.notes}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Ordered Items Section */}
                            <div className="order-detail-section">
                                <h3>Artigos Encomendados</h3>
                                <table className="order-items-table">
                                    <thead>
                                        <tr>
                                            <th>Artigo</th>
                                            <th>Preço Unit.</th>
                                            <th>Qtd.</th>
                                            <th>Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(selectedOrder.items || []).map((item) => (
                                            <tr key={item.id}>
                                                <td>
                                                    <div className="item-preview">
                                                        <div className="item-no-image">
                                                            <ShoppingBag size={16} />
                                                        </div>
                                                        <div>
                                                            <strong>{item.productName}</strong>
                                                            {item.variantDescription && (
                                                                <span style={{ display: 'block', fontSize: '11px', color: '#64748b' }}>
                                                                    {item.variantDescription}
                                                                </span>
                                                            )}
                                                            {(item.customName || item.customNumber) && (
                                                                <span style={{ display: 'block', fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                                                                    <strong>Personalização:</strong> {item.customName || '-'} / Nº {item.customNumber || '-'}
                                                                </span>
                                                            )}
                                                            {item.kitPieceSelectionsJson && (() => {
                                                                try {
                                                                    const parsed = JSON.parse(item.kitPieceSelectionsJson);
                                                                    if (!parsed || parsed.length === 0) return null;
                                                                    return (
                                                                        <div style={{ marginTop: '8px', padding: '8px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                                                            <strong style={{ fontSize: '11px', color: '#0f172a', display: 'block', marginBottom: '4px' }}>Peças do Kit:</strong>
                                                                            <ul style={{ margin: 0, paddingLeft: '16px', fontSize: '11px', color: '#475569' }}>
                                                                                {parsed.map((kp, idx) => {
                                                                                    const pName = kp.PieceName || kp.pieceName || `Peça ${kp.KitPieceId || kp.kitPieceId}`;
                                                                                    const pSize = kp.SelectedSize || kp.selectedSize || '';
                                                                                    const cName = kp.CustomName || kp.customName;
                                                                                    const cNumber = kp.CustomNumber || kp.customNumber;

                                                                                    return (
                                                                                        <li key={idx} style={{ marginBottom: '2px' }}>
                                                                                            <strong>{pName}</strong> - Tamanho: {pSize}
                                                                                            {(cName || cNumber) && (
                                                                                                <span> (Personalização: {cName || '-'} / Nº {cNumber || '-'})</span>
                                                                                            )}
                                                                                        </li>
                                                                                    );
                                                                                })}
                                                                            </ul>
                                                                        </div>
                                                                    );
                                                                } catch (e) {
                                                                    return null;
                                                                }
                                                            })()}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>{(item.unitPrice || 0).toFixed(2)}€</td>
                                                <td>{item.quantity}</td>
                                                <td>{(item.lineTotal || 0).toFixed(2)}€</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <div className="order-total-row">
                                    Total: {(selectedOrder.totalAmount || 0).toFixed(2)}€
                                </div>
                            </div>

                            {/* Update Status Section */}
                            <div className="order-detail-section">
                                <h3>Atualizar Estado da Encomenda</h3>
                                <div className="status-update-panel">
                                    <div className="form-group">
                                        <label>Estado Atual: <strong style={{ color: 'var(--color-primary)' }}>{selectedOrder.status}</strong></label>
                                        <select
                                            className="filter-select"
                                            style={{ width: '100%', marginTop: '6px' }}
                                            value={modalStatusValue}
                                            onChange={(e) => setModalStatusValue(e.target.value)}
                                        >
                                            <option value="Pendente">Pendente</option>
                                            <option value="Pago">Pago</option>
                                            <option value="EmProcessamento">Em Processamento</option>
                                            <option value="Enviado">Enviado</option>
                                            <option value="Entregue">Entregue</option>
                                            <option value="Cancelado">Cancelado</option>
                                        </select>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn-update"
                                        disabled={updatingOrderStatus || modalStatusValue === selectedOrder.status}
                                        onClick={() => handleUpdateOrderStatus(selectedOrder.id, modalStatusValue)}
                                    >
                                        {updatingOrderStatus ? 'A Guardar...' : 'Atualizar Estado'}
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn-cancel" onClick={() => { setShowOrderModal(false); setSelectedOrder(null); }}>
                                Fechar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StoreManager;
